import {
	canDeleteEngageMessage,
	canEditEngageMessage,
	ENGAGE_CONNECT_NETWORKS,
	engageAccountCanSend,
	engageConversationTitle,
	engagePersonLabel,
	engageMessageDeletedCopy,
	engageNetworkCatalog,
	engageInboxMatchesQuery,
	engageLoginPhoneHint,
	mergeEngageInboxParticipants,
	engageUniqueInboxRows,
	engageFormatText,
	engageSplitHttpLinks,
	engageIsPlaceholderBody,
	engageLooksLikeImageMime,
	ENGAGE_CONTACT_MIME,
	ENGAGE_LOCATION_MIME,
	ENGAGE_POLL_MIME,
	engageWhatsAppAccountKey,
	engageWhatsAppPhone,
	normalizeEngageLoginPhone,
	type EngageInboxNetwork,
} from "@bafe/types";
import type {
	EngageAccountDto,
	EngageCatalogItemDto,
	EngageConversationDto,
	EngageMessageDto,
} from "@bafe/api-client";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type MouseEvent, type PointerEvent, type ReactElement } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ArrowDown, ArrowUp, AtSign, ChevronLeft, CloseCircle, Document, DocumentDownload, Gallery, Menu, More, Paperclip, Reply, Search, Send2, TickCircle } from "reicon-react";

import { EngageNetworkIcon } from "../../components/EngageNetworkIcon";
import { EngagePinIcon } from "../../components/EngagePinIcon";
import { EngageAudioPlayer, EngageVoiceCapture, EngageVoiceMic } from "../../components/EngageVoice";
import { Popup } from "../../components/Popup";
import { QrCode } from "../../components/QrCode";
import { api } from "../../lib/api";
import { dissolveElement, resetDissolve } from "../../lib/dissolve";
import { applyEngageLiveFrame } from "../../lib/engage-live";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";
import { useSession } from "../../lib/session";
import { isoDay, labelOf, stamp } from "../../lib/when.js";

type NetworkFilter = "all" | "whatsapp" | "telegram" | "linkedin";
type KindFilter = "all" | "chats" | "groups";

const CHAT_NETWORKS = ["whatsapp", "telegram", "linkedin"] as const;

export function EngageOverview() {
	return <EngageMessenger />;
}

export function EngageInbox() {
	return <EngageMessenger />;
}

export function EngageConversation() {
	return <EngageMessenger />;
}

function EngageMessenger() {
	const { id } = useParams();
	const conversationId = id ?? "";
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const session = useSession();
	const logRef = useRef<HTMLOListElement>(null);
	const pinnedToLatest = useRef(true);
	const [awayFromLatest, setAwayFromLatest] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);
	const holdRef = useRef<number | null>(null);
	const dragDepth = useRef(0);
	const [query, setQuery] = useState("");
	const [kindFilter, setKindFilter] = useState<KindFilter>("all");
	const [filter, setFilter] = useState<NetworkFilter>("all");
	const [body, setBody] = useState("");
	const [drafts, setDrafts] = useState<Array<{ id: string; file: File; url: string; kind: "image" | "file" }>>([]);
	const [attachOpen, setAttachOpen] = useState(false);
	const [dragOver, setDragOver] = useState(false);
	const [pendingEdit, setPendingEdit] = useState<{ id: string; body: string } | null>(null);
	const [pendingReply, setPendingReply] = useState<{ id: string; name: string; body: string } | null>(null);
	const [msgMenu, setMsgMenu] = useState<{
		id: string;
		body: string;
		quoteName: string;
		canEdit: boolean;
		canDelete: boolean;
		x: number;
		y: number;
	} | null>(null);
	const [pendingMsgDelete, setPendingMsgDelete] = useState<string | null>(null);
	const [vanished, setVanished] = useState<string[]>([]);
	const [chatMenu, setChatMenu] = useState<{ id: string; name: string; pinned: boolean; x: number; y: number } | null>(
		null,
	);
	const [pendingChatDelete, setPendingChatDelete] = useState<{ id: string; name: string } | null>(null);
	const [viewingId, setViewingId] = useState("");
	const [membersOpen, setMembersOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [voiceOpen, setVoiceOpen] = useState(false);
	const [quoteHitId, setQuoteHitId] = useState("");
	const [findOpen, setFindOpen] = useState(false);
	const [find, setFind] = useState("");
	const [findHitId, setFindHitId] = useState("");
	const quoteHitTimerRef = useRef<number | null>(null);
	const ignoreClickRef = useRef(false);
	const organisationId = session.data?.activeOrganisationId;

	function clearHold() {
		if (holdRef.current !== null) window.clearTimeout(holdRef.current);
		holdRef.current = null;
	}

	function clearDrafts() {
		setDrafts((current) => {
			for (const item of current) {
				if (item.url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(item.url);
			}
			return [];
		});
	}

	function removeDraft(id: string) {
		setDrafts((current) => {
			const item = current.find((draft) => draft.id === id);
			if (item?.url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(item.url);
			return current.filter((draft) => draft.id !== id);
		});
	}

	function pickFiles(list: FileList | null, photosOnly = false) {
		setAttachOpen(false);
		if (!list?.length) return;
		const extra: Array<{ id: string; file: File; url: string; kind: "image" | "file" }> = [];
		for (const file of Array.from(list)) {
			if (file.size > ENGAGE_FILE_MAX_BYTES) {
				setError("That file is larger than 12 MB.");
				continue;
			}
			const image = engageLooksLikeImageMime(file.type === "image/jpg" ? "image/jpeg" : file.type) && ENGAGE_IMAGE_TYPES.has(file.type === "image/jpg" ? "image/jpeg" : file.type);
			if (photosOnly && !image) continue;
			extra.push({
				id: `${file.name}-${file.size}-${file.lastModified}-${extra.length}`,
				file,
				kind: image ? "image" : "file",
				url:
					image && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : "",
			});
		}
		if (extra.length) setError(null);
		setDrafts((current) => [...current, ...extra].slice(0, 10));
	}

	function onChatDragEnter(event: DragEvent<HTMLDivElement>) {
		if (composeBlocked || pendingEdit || !event.dataTransfer?.types.includes("Files")) return;
		event.preventDefault();
		dragDepth.current += 1;
		setDragOver(true);
	}

	function onChatDragOver(event: DragEvent<HTMLDivElement>) {
		if (composeBlocked || pendingEdit || !event.dataTransfer?.types.includes("Files")) return;
		event.preventDefault();
		event.dataTransfer.dropEffect = "copy";
	}

	function onChatDragLeave(event: DragEvent<HTMLDivElement>) {
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			dragDepth.current = 0;
			setDragOver(false);
			return;
		}
		dragDepth.current = Math.max(0, dragDepth.current - 1);
		if (dragDepth.current === 0) setDragOver(false);
	}

	function onChatDrop(event: DragEvent<HTMLDivElement>) {
		event.preventDefault();
		dragDepth.current = 0;
		setDragOver(false);
		if (composeBlocked || pendingEdit) return;
		pickFiles(event.dataTransfer?.files ?? null);
	}

	function openChatMenu(item: EngageConversationDto, x: number, y: number) {
		setMsgMenu(null);
		setAttachOpen(false);
		setChatMenu({ id: item.id, name: conversationTitle(item), pinned: item.pinned === true, x, y });
	}

	function onChatContextMenu(event: MouseEvent<HTMLAnchorElement>, item: EngageConversationDto) {
		event.preventDefault();
		event.stopPropagation();
		openChatMenu(item, event.clientX, event.clientY);
	}

	function onChatPointerDown(event: PointerEvent<HTMLAnchorElement>, item: EngageConversationDto) {
		if (event.pointerType === "mouse") return;
		const x = event.clientX;
		const y = event.clientY;
		clearHold();
		holdRef.current = window.setTimeout(() => {
			ignoreClickRef.current = true;
			openChatMenu(item, x, y);
		}, 500);
	}

	function onChatClick(event: MouseEvent<HTMLAnchorElement>) {
		if (ignoreClickRef.current) {
			event.preventDefault();
			ignoreClickRef.current = false;
			return;
		}
		event.currentTarget.focus({ preventScroll: true });
	}

	function openMsgMenu(message: EngageMessageDto, x: number, y: number, network?: string, quoteName?: string) {
		if (message.deletedAt) return;
		setChatMenu(null);
		setMsgMenu({
			id: message.id,
			body: message.body,
			quoteName: message.direction === "outbound" ? "You" : message.senderName?.trim() || quoteName || "Chat",
			canEdit: canEditEngageMessage({
				direction: message.direction,
				messageType: message.messageType,
				deletedAt: message.deletedAt,
				createdAt: message.createdAt,
				sendStatus: message.sendStatus,
				network,
			}),
			canDelete: canDeleteEngageMessage({
				direction: message.direction,
				deletedAt: message.deletedAt,
				createdAt: message.createdAt,
				sendStatus: message.sendStatus,
				network,
			}),
			x,
			y,
		});
	}

	function startEdit(id: string, text: string) {
		clearDrafts();
		setAttachOpen(false);
		setPendingReply(null);
		setPendingEdit({ id, body: text });
		setBody(text);
		setMsgMenu(null);
	}

	function startReply(id: string, name: string, text: string) {
		setPendingEdit(null);
		setPendingReply({ id, name: name || "Chat", body: text });
		setMsgMenu(null);
	}

	function cancelEdit() {
		setPendingEdit(null);
		setBody("");
	}

	function cancelReply() {
		setPendingReply(null);
	}

	function clearQuoteHitTimer() {
		if (quoteHitTimerRef.current !== null) {
			window.clearTimeout(quoteHitTimerRef.current);
			quoteHitTimerRef.current = null;
		}
	}

	function jumpToMessage(id: string | null | undefined) {
		if (!id) return;
		clearQuoteHitTimer();
		setQuoteHitId(id);
		const scrollId =
			messages.data?.length ? engageListItemIdForMessage(messages.data, id) : id;
		document.getElementById(`engage-msg-${scrollId}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
		quoteHitTimerRef.current = window.setTimeout(() => {
			setQuoteHitId("");
			quoteHitTimerRef.current = null;
		}, 2000);
	}

	function scrollToFindMessage(messageId: string) {
		setFindHitId(messageId);
		const scrollId = messages.data?.length ? engageListItemIdForMessage(messages.data, messageId) : messageId;
		document.getElementById(`engage-msg-${scrollId}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
	}

	function closeFind() {
		setFindOpen(false);
		setFind("");
		setFindHitId("");
	}

	function nearLatest(log: HTMLElement): boolean {
		return log.scrollHeight - log.scrollTop - log.clientHeight < 96;
	}

	function stickToLatest(behavior: ScrollBehavior = "auto") {
		const log = logRef.current;
		if (!log?.scrollTo) return;
		pinnedToLatest.current = true;
		log.scrollTo({ top: log.scrollHeight, behavior });
	}

	function syncAwayFromLatest() {
		const log = logRef.current;
		if (!log) return;
		const away = !nearLatest(log);
		pinnedToLatest.current = !away;
		setAwayFromLatest((current) => (current === away ? current : away));
	}

	const conversations = useQuery({
		queryKey: queryKeys.engageConversations,
		queryFn: () => api.listEngageConversations(),
		refetchInterval: visibilityInterval,
	});
	const messages = useQuery({
		queryKey: queryKeys.engageMessages(conversationId),
		queryFn: () => api.listEngageMessages(conversationId),
		enabled: Boolean(conversationId),
		refetchInterval: conversationId ? visibilityInterval : false,
	});
	useLayoutEffect(() => {
		if (findOpen && find.trim()) return;
		if (pinnedToLatest.current) stickToLatest();
	}, [conversationId, find, findOpen, messages.data]);

	useEffect(() => {
		const log = logRef.current;
		if (!log) return;
		const restick = () => {
			if (pinnedToLatest.current) stickToLatest();
		};
		log.addEventListener("load", restick, true);
		const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(restick);
		resize?.observe(log);
		return () => {
			log.removeEventListener("load", restick, true);
			resize?.disconnect();
		};
	}, [conversationId, messages.data]);

	useEffect(() => {
		pinnedToLatest.current = true;
		setAwayFromLatest(false);
		setMembersOpen(false);
		setPendingEdit(null);
		setPendingReply(null);
		setMsgMenu(null);
		setPendingMsgDelete(null);
		setChatMenu(null);
		setPendingChatDelete(null);
		setAttachOpen(false);
		setViewingId("");
		setBody("");
		clearDrafts();
		setQuoteHitId("");
		clearQuoteHitTimer();
		setFindOpen(false);
		setFind("");
		setFindHitId("");
		setVanished([]);
	}, [conversationId]);

	useEffect(() => () => clearQuoteHitTimer(), []);

	useEffect(() => {
		if (!msgMenu && !attachOpen && !chatMenu) return;
		const close = () => {
			setMsgMenu(null);
			setChatMenu(null);
			setAttachOpen(false);
		};
		window.addEventListener("pointerdown", close);
		return () => window.removeEventListener("pointerdown", close);
	}, [attachOpen, chatMenu, msgMenu]);

	const lastInboundId =
		messages.data?.reduce((id, message) => (message.direction === "inbound" ? message.id : id), "") ?? "";

	useEffect(() => {
		markOpenEngageChatRead(conversationId, lastInboundId, queryClient);
	}, [conversationId, lastInboundId, queryClient]);

	useEffect(() => {
		if (typeof document === "undefined") return;
		const onVisible = () => {
			if (document.visibilityState === "visible") markOpenEngageChatRead(conversationId, lastInboundId, queryClient);
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => document.removeEventListener("visibilitychange", onVisible);
	}, [conversationId, lastInboundId, queryClient]);

	useEffect(() => {
		if (!organisationId || typeof window === "undefined") return;
		const protocol = window.location.protocol === "https:" ? "wss" : "ws";
		const socket = new WebSocket(
			`${protocol}://${window.location.host}/api/v1/engage/ws?organisationId=${encodeURIComponent(organisationId)}`,
		);
		socket.onmessage = (event) => applyEngageLiveFrame(queryClient, String(event.data));
		return () => socket.close();
	}, [organisationId, queryClient]);

	const reply = useMutation({
		mutationFn: async (input: {
			text: string;
			files: Array<{ file: File }>;
			replyToMessageId?: string;
			replyName?: string;
			replyBody?: string;
		}) => {
			if (input.files.length) {
				let last: Awaited<ReturnType<typeof api.replyEngageConversation>> | undefined;
				for (const [index, draft] of input.files.entries()) {
					const image = await fileToEngageAttachment(draft.file);
					last = await api.replyEngageConversation(
						conversationId,
						index === input.files.length - 1 ? input.text : "",
						crypto.randomUUID(),
						image,
						input.replyToMessageId,
						input.replyName,
						input.replyBody,
					);
				}
				return last;
			}
			return api.replyEngageConversation(
				conversationId,
				input.text,
				crypto.randomUUID(),
				undefined,
				input.replyToMessageId,
				input.replyName,
				input.replyBody,
			);
		},
		onMutate: async (input) => {
			pinnedToLatest.current = true;
			setAwayFromLatest(false);
			setError(null);
			setBody("");
			clearDrafts();
			setPendingReply(null);
			const firstFile = input.files[0]?.file;
			const audio = Boolean(firstFile?.type.startsWith("audio/"));
			const optimistic: EngageMessageDto = {
				id: `local-${crypto.randomUUID()}`,
				direction: "outbound",
				body: input.text || (audio ? "Voice message" : firstFile?.name || (input.files.length ? "File" : "")),
				sendStatus: "sending",
				messageType: input.files.length ? "media" : "text",
				mediaMime: firstFile?.type || null,
				mediaName: firstFile?.name || null,
				createdAt: new Date().toISOString(),
				replyTo: input.replyToMessageId
					? { messageId: input.replyToMessageId, name: input.replyName ?? null, body: input.replyBody ?? null }
					: null,
			};
			await queryClient.cancelQueries({ queryKey: queryKeys.engageMessages(conversationId) });
			const previous = queryClient.getQueryData<EngageMessageDto[]>(queryKeys.engageMessages(conversationId));
			queryClient.setQueryData(queryKeys.engageMessages(conversationId), (rows: EngageMessageDto[] | undefined) => [
				...(rows ?? []),
				optimistic,
			]);
			queryClient.setQueryData(queryKeys.engageConversations, (rows: EngageConversationDto[] | undefined) =>
				rows?.map((row) =>
					row.id === conversationId
						? { ...row, lastMessage: optimistic.body, lastMessageAt: optimistic.createdAt }
						: row,
				),
			);
			return { previous, optimisticId: optimistic.id };
		},
		onError: (caught, input, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKeys.engageMessages(conversationId), context.previous);
			}
			setBody(input.text);
			if (input.replyToMessageId) {
				setPendingReply({
					id: input.replyToMessageId,
					name: input.replyName || "Chat",
					body: input.replyBody || "",
				});
			}
			setError(presentError(caught).title);
		},
		onSuccess: (sent, _input, context) => {
			if (sent) {
				queryClient.setQueryData(queryKeys.engageMessages(conversationId), (rows: EngageMessageDto[] | undefined) => {
					const next = (rows ?? []).filter((row) => row.id !== context?.optimisticId);
					if (next.some((row) => row.id === sent.id)) return next;
					return [...next, sent];
				});
			}
			void queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		},
	});

	const editMsg = useMutation({
		mutationFn: ({ id, text }: { id: string; text: string }) => api.editEngageMessage(conversationId, id, text),
		onSuccess: async () => {
			setPendingEdit(null);
			setBody("");
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageMessages(conversationId) });
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const deleteMsg = useMutation({
		mutationFn: (id: string) => api.deleteEngageMessage(conversationId, id),
		onSuccess: async () => {
			setPendingMsgDelete(null);
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageMessages(conversationId) });
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		},
		onError: (caught, id) => {
			setVanished((current) => current.filter((item) => item !== id));
			setError(presentError(caught).title);
		},
	});

	const pinChat = useMutation({
		mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => api.pinEngageConversation(id, pinned),
		onSuccess: async () => {
			setChatMenu(null);
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const deleteChat = useMutation({
		mutationFn: (id: string) => api.deleteEngageConversation(id),
		onSuccess: async (_void, id) => {
			setPendingChatDelete(null);
			setChatMenu(null);
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
			if (id === conversationId) navigate("/engage/inbox");
		},
		onError: (caught, id) => {
			const row = document.querySelector(`[href$="/engage/inbox/${id}"]`)?.closest("li");
			resetDissolve(row instanceof HTMLElement ? row : null);
			setError(presentError(caught).title);
		},
	});

	const listed = useMemo(() => engageUniqueInboxRows(conversations.data ?? []), [conversations.data]);

	useEffect(() => {
		if (!conversationId || !conversations.isSuccess) return;
		if (listed.some((item) => item.id === conversationId)) return;
		navigate("/engage/inbox", { replace: true });
	}, [conversationId, conversations.isSuccess, listed, navigate]);

	useEffect(() => {
		setVoiceOpen(false);
	}, [conversationId]);

	const visible = useMemo(() => {
		const needle = query.trim();
		return listed
			.filter((conversation) => {
				if (filter !== "all" && conversation.network !== filter) return false;
				if (kindFilter === "chats" && conversation.kind === "group") return false;
				if (kindFilter === "groups" && conversation.kind !== "group") return false;
				const hasThread =
					Boolean(conversation.lastMessage?.trim()) ||
					Boolean(conversation.lastMessageAt) ||
					Boolean(conversation.pinned) ||
					Boolean(conversation.unread) ||
					conversation.id === conversationId;
				if (!needle) return hasThread;
				const handle = conversation.customerHandle ?? "";
				const phone = conversationPhone(conversation);
				const title = conversationTitle(conversation);
				return engageInboxMatchesQuery(
					[
						title,
						conversation.customerName,
						conversation.accountName,
						labelOf(conversation.network),
						conversation.lastMessage ?? "",
						handle,
						phone,
					].join(" "),
					needle,
				);
			})
			.sort((left, right) => {
				if (Boolean(left.pinned) !== Boolean(right.pinned)) return left.pinned ? -1 : 1;
				return (right.lastMessageAt ?? "").localeCompare(left.lastMessageAt ?? "");
			});
	}, [conversationId, filter, kindFilter, listed, query]);
	const multiAccountNetworks = useMemo(() => {
		const counts = new Map<string, Set<string>>();
		for (const row of listed) {
			const ids = counts.get(row.network) ?? new Set<string>();
			ids.add(row.accountId);
			counts.set(row.network, ids);
		}
		return new Set([...counts.entries()].filter(([, ids]) => ids.size > 1).map(([network]) => network));
	}, [listed]);

	const conversation = listed.find((item) => item.id === conversationId);
	const closed = conversation?.replyWindowOpen === false;
	const accountOffline = Boolean(conversation?.accountStatus && !engageAccountCanSend(conversation.accountStatus));
	const composeBlocked = closed || accountOffline;
	const isGroup = conversation?.kind === "group";
	const groupPeople = useMemo(() => {
		const fromChat = conversation?.participants ?? [];
		const fromThread = (messages.data ?? [])
			.map((message) => {
				const providerId = message.senderHandle?.trim() ?? "";
				const displayName = usableSenderName(message.senderName) || providerId;
				return providerId ? { providerId, displayName } : null;
			})
			.filter((person): person is { providerId: string; displayName: string } => person != null);
		return mergeEngageInboxParticipants(fromChat, fromThread).sort((left, right) => {
			const named = (name: string) => Number(Boolean(name.trim() && !/^\d+$/.test(name.trim())));
			const rank = named(right.displayName) - named(left.displayName);
			if (rank !== 0) return rank;
			return participantLabel(left, conversation?.network).localeCompare(participantLabel(right, conversation?.network));
		});
	}, [conversation?.network, conversation?.participants, messages.data]);

	const findMatches = useMemo(() => {
		const needle = find.trim().toLowerCase();
		if (!needle) return [];
		return (messages.data ?? []).filter((message) => {
			if (message.deletedAt) return false;
			const text = [message.body, message.mediaName].filter(Boolean).join(" ").toLowerCase();
			return text.includes(needle);
		});
	}, [find, messages.data]);
	const findHits = useMemo(() => [...findMatches].reverse(), [findMatches]);
	const findMatchKey = findMatches.map((message) => message.id).join(",");
	const activeMessageHitId = quoteHitId || findHitId;

	useEffect(() => {
		if (!findOpen || !findMatches.length) {
			if (!findMatches.length) setFindHitId("");
			return;
		}
		const latest = findMatches[findMatches.length - 1];
		if (!latest) return;
		setFindHitId(latest.id);
		requestAnimationFrame(() => scrollToFindMessage(latest.id));
	}, [findOpen, findMatchKey, findMatches]);

	function stepFindHit(delta: number) {
		if (!findMatches.length) return;
		const index = findMatches.findIndex((message) => message.id === findHitId);
		const next = findMatches[(index + delta + findMatches.length) % findMatches.length];
		if (next) scrollToFindMessage(next.id);
	}

	return (
		<div className={conversationId ? "team-app is-chat" : "team-app"}>
			<section className="team-list" aria-label="Inbox">
				<div className="team-list-head">
					<label className="team-search">
						<span className="sr">Search chats</span>
						<Search size={16} aria-hidden="true" />
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Search"
							autoComplete="off"
						/>
					</label>
				</div>
				<div className="engage-filters" role="tablist" aria-label="Inbox">
					<FilterTab current={kindFilter} value="all" onSelect={setKindFilter}>
						All
					</FilterTab>
					<FilterTab current={kindFilter} value="chats" onSelect={setKindFilter}>
						Chats
					</FilterTab>
					<FilterTab current={kindFilter} value="groups" onSelect={setKindFilter}>
						Groups
					</FilterTab>
				</div>
				<div className="engage-filters engage-filters-net" role="tablist" aria-label="Network">
					<FilterTab current={filter} value="all" onSelect={setFilter}>
						All networks
					</FilterTab>
					{CHAT_NETWORKS.map((network) => (
						<FilterTab current={filter} value={network} key={network} onSelect={setFilter}>
							{labelOf(network)}
						</FilterTab>
					))}
				</div>
				{error && <p className="err team-list-error">{error}</p>}
				<div className="team-scroll">
					{conversations.isPending ? (
						<p className="empty">Loading inbox.</p>
					) : conversations.isError ? (
						<p className="err">{presentError(conversations.error).title}</p>
					) : !listed.length ? (
						<p className="empty">
							No chats yet. After WhatsApp signs in, existing chats and contacts fill this list. Search by name or number.{" "}
							<Link to="/engage/accounts">Connect a WhatsApp or Telegram account</Link>.
						</p>
					) : (
						visible.length ? (
							<ul className="team-rows">
								{visible.map((item) => (
									<li key={item.id}>
										<ConversationRow
											item={item}
											active={item.id === conversationId}
											showAccount={multiAccountNetworks.has(item.network)}
											onContextMenu={onChatContextMenu}
											onPointerDown={onChatPointerDown}
											onPointerUp={clearHold}
											onClick={onChatClick}
										/>
									</li>
								))}
							</ul>
						) : (
							<p className="empty">
								{query.trim()
									? "No chats match that search."
									: kindFilter === "groups"
										? "No groups yet."
										: filter === "all"
											? "No chats yet."
											: `No ${labelOf(filter)} chats yet.`}
							</p>
						)
					)}
				</div>
				<div className="team-fab-wrap">
					<button
						type="button"
						className="team-fab"
						aria-label="Connect chat accounts"
						onClick={() => navigate("/engage/accounts")}
					>
						<AtSign size={20} aria-hidden="true" />
					</button>
				</div>
			</section>
			<section className="team-chat" aria-label="Chat">
				{conversationId && conversation ? (
					<>
						<div
							className={dragOver ? "team-chat-col is-drop" : "team-chat-col"}
							onDragEnter={onChatDragEnter}
							onDragOver={onChatDragOver}
							onDragLeave={onChatDragLeave}
							onDrop={onChatDrop}
						>
							{dragOver ? (
								<div className="engage-drop" role="status">
									Drop files here
								</div>
							) : null}
						<header
							className="team-chat-head"
							onContextMenu={(event) => {
								if (!conversation) return;
								event.preventDefault();
								openChatMenu(conversation, event.clientX, event.clientY);
							}}
						>
							<Link className="team-back" to="/engage/inbox" aria-label="Back to inbox">
								<ChevronLeft size={20} aria-hidden="true" />
							</Link>
							{findOpen ? (
								<>
									<span className={`team-avatar engage-avatar tone-${toneOf(conversationTitle(conversation))}`} aria-hidden="true">
										{initials(conversationTitle(conversation))}
										<span className={`engage-net net-${conversation.network}`}>{networkMark(conversation.network)}</span>
									</span>
									<label className="team-find">
										<Search size={16} aria-hidden="true" />
										<span className="sr">Search messages</span>
										<input
											value={find}
											onChange={(event) => setFind(event.target.value)}
											onKeyDown={(event) => {
												if (event.key === "ArrowUp") {
													event.preventDefault();
													stepFindHit(-1);
												} else if (event.key === "ArrowDown") {
													event.preventDefault();
													stepFindHit(1);
												} else if (event.key === "Escape") {
													event.preventDefault();
													closeFind();
												}
											}}
											placeholder="Search"
											autoComplete="off"
											autoFocus
										/>
									</label>
								</>
							) : isGroup ? (
								<button
									className="team-chat-info"
									type="button"
									aria-haspopup="dialog"
									aria-expanded={membersOpen}
									onClick={() => setMembersOpen(true)}
								>
									<span className={`team-avatar engage-avatar tone-${toneOf(conversationTitle(conversation))}`} aria-hidden="true">
										{initials(conversationTitle(conversation))}
										<span className={`engage-net net-${conversation.network}`}>{networkMark(conversation.network)}</span>
									</span>
									<span className="team-chat-who">
										<strong>{conversationTitle(conversation)}</strong>
										<span>{groupPeople.length ? `${groupPeople.length} participants` : "Group"}</span>
									</span>
								</button>
							) : (
								<div className="team-chat-info">
									<span className={`team-avatar engage-avatar tone-${toneOf(conversationTitle(conversation))}`} aria-hidden="true">
										{initials(conversationTitle(conversation))}
										<span className={`engage-net net-${conversation.network}`}>{networkMark(conversation.network)}</span>
									</span>
									<span className="team-chat-who">
										<strong>{conversationTitle(conversation)}</strong>
										<span>{conversationPhone(conversation)}</span>
									</span>
								</div>
							)}
							{findOpen ? (
								<>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Previous match"
										disabled={!findMatches.length}
										onClick={() => stepFindHit(-1)}
									>
										<ArrowUp size={16} aria-hidden="true" />
									</button>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Next match"
										disabled={!findMatches.length}
										onClick={() => stepFindHit(1)}
									>
										<ArrowDown size={16} aria-hidden="true" />
									</button>
									<button className="team-icon-btn" type="button" aria-label="Close search" onClick={closeFind}>
										<CloseCircle size={18} aria-hidden="true" />
									</button>
								</>
							) : (
								<>
									<button className="team-icon-btn" type="button" aria-label="Search messages" onClick={() => setFindOpen(true)}>
										<Search size={18} aria-hidden="true" />
									</button>
									<button
										className="team-icon-btn"
										type="button"
										aria-label="Conversation menu"
										onClick={(event) => {
											if (!conversation) return;
											const box = event.currentTarget.getBoundingClientRect();
											openChatMenu(conversation, box.right - 168, box.bottom);
										}}
									>
										<More size={18} aria-hidden="true" />
									</button>
								</>
							)}
						</header>
						{findOpen && find.trim() ? (
							<ul className="team-hits" role="listbox" aria-label="Search results">
								{findHits.length === 0 ? (
									<li className="team-hits-empty">No messages match that search.</li>
								) : (
									findHits.map((message) => {
										const name =
											message.direction === "outbound"
												? "You"
												: senderLabel(message, conversation.participants, listed, messages.data ?? []);
										const preview = message.body || message.mediaName || "";
										return (
											<li key={message.id} role="presentation">
												<button
													type="button"
													role="option"
													aria-selected={message.id === findHitId}
													className={message.id === findHitId ? "team-hit active" : "team-hit"}
													onClick={() => scrollToFindMessage(message.id)}
												>
													<span className={`team-avatar tone-${toneOf(name)}`} aria-hidden="true">
														{initials(name)}
													</span>
													<span className="team-hit-main">
														<strong>{name}</strong>
														<span>{engageFindHighlight(preview, find)}</span>
													</span>
													<time dateTime={message.createdAt}>{listTime(message.createdAt)}</time>
												</button>
											</li>
										);
									})
								)}
							</ul>
						) : null}
							<div className="team-thread">
								{messages.isPending ? (
									<p className="empty">Loading conversation.</p>
								) : messages.isError ? (
									<p className="err">{presentError(messages.error).title}</p>
								) : !messages.data?.length ? (
									<p className="empty">No messages in this chat.</p>
								) : (
									<ol className="team-log engage-log" ref={logRef} onScroll={syncAwayFromLatest}>
										{threadItems(messages.data).map((item, index, items) => {
											if (item.kind === "album" && item.messages.every((message) => vanished.includes(message.id))) {
												return null;
											}
											if (item.kind === "message" && vanished.includes(item.message.id)) return null;
											const lead =
												item.kind === "album"
													? item.messages[0]
													: item.kind === "message"
														? item.message
														: undefined;
											const named = Boolean(
												lead && showEngageSender(lead, previousEngageThreadItem(items, index), isGroup),
											);
											const follow = Boolean(lead && isGroup && lead.direction !== "outbound" && !named);
											const senderName =
												named && lead
													? senderLabel(lead, conversation.participants, listed, messages.data)
													: "";
											return item.kind === "day" ? (
												<li className="team-day" key={item.id}>
													{item.label}
												</li>
											) : item.kind === "album" ? (
												<li
													id={`engage-msg-${item.id}`}
													className={`${item.messages[0]?.direction === "outbound" ? "out" : "in"} has-media${engageThreadItemHit(item, activeMessageHitId) ? " hit" : ""}${follow ? " is-follow" : ""}`}
													key={item.id}
												>
													<span className="bubble has-media">
														{senderName ? <strong className="engage-sender">{senderName}</strong> : null}
														<div className={`team-album is-${Math.min(item.messages.length, 6)}`}>
															{item.messages.map((message) =>
																message.mediaSha256 ? (
																	<EngageMedia
																		key={message.id}
																		conversationId={conversationId}
																		message={message}
																		onOpen={() => setViewingId(message.id)}
																	/>
																) : (
																	<span className="team-media-wait" key={message.id}>
																		Photo
																	</span>
																),
															)}
														</div>
														{engageAlbumCaption(item.messages) ? (
															<EngageRichText
																body={engageAlbumCaption(item.messages)}
																entities={item.messages.find((message) => engageVisibleCaption(message.body, message.mediaName))?.textEntities}
															/>
														) : null}
														<time dateTime={item.messages[item.messages.length - 1]?.createdAt}>
															{clockTime(item.messages[item.messages.length - 1]?.createdAt ?? "")}
														</time>
													</span>
												</li>
											) : (
												<li
													id={`engage-msg-${item.message.id}`}
													className={`${item.message.direction === "outbound" ? "out" : "in"}${engageThreadItemHit(item, activeMessageHitId) ? " hit" : ""}${pendingEdit?.id === item.message.id ? " is-editing" : ""}${item.message.messageType === "media" && !item.message.deletedAt ? " has-media" : ""}${follow ? " is-follow" : ""}`}
													key={item.message.id}
												>
													<span
														className={`${item.message.deletedAt ? "bubble is-deleted" : "bubble"}${item.message.messageType === "media" && !item.message.deletedAt ? " has-media" : ""}`}
														onContextMenu={(event) => {
															if (item.message.deletedAt) return;
															event.preventDefault();
															event.stopPropagation();
															openMsgMenu(
																item.message,
																event.clientX,
																event.clientY,
																conversation?.network,
																isGroup
																	? senderLabel(
																			item.message,
																			conversation.participants,
																			listed,
																			messages.data,
																		) || "Member"
																	: conversation?.customerName,
															);
														}}
														onPointerDown={(event: PointerEvent<HTMLSpanElement>) => {
															if (event.pointerType === "mouse") return;
															if (item.message.deletedAt) return;
															const x = event.clientX;
															const y = event.clientY;
															clearHold();
															holdRef.current = window.setTimeout(() => {
																openMsgMenu(
																	item.message,
																	x,
																	y,
																	conversation?.network,
																	isGroup
																		? senderLabel(
																				item.message,
																				conversation.participants,
																				listed,
																				messages.data,
																			) || "Member"
																		: conversation?.customerName,
																);
															}, 500);
														}}
														onPointerUp={clearHold}
														onPointerCancel={clearHold}
														onPointerLeave={clearHold}
													>
														{item.message.deletedAt ? (
															<>
																{senderName ? <strong className="engage-sender">{senderName}</strong> : null}
																<span className="team-bubble-body">{engageMessageDeletedCopy()}</span>
															</>
														) : (
															<>
																{senderName ? <strong className="engage-sender">{senderName}</strong> : null}
																{item.message.replyTo ? (
																	<button
																		className="engage-quote"
																		type="button"
																		onClick={() => jumpToMessage(item.message.replyTo?.messageId)}
																	>
																		<strong>
																			{item.message.replyTo.name?.trim() ||
																				(item.message.direction === "outbound"
																					? isGroup
																						? "Member"
																						: conversation?.customerName
																					: "You") ||
																				"Chat"}
																		</strong>
																		<span>{item.message.replyTo.body || "Message"}</span>
																	</button>
																) : null}
																{isEngageSpecial(item.message) ? <EngageSpecial message={item.message} /> : null}
																{item.message.messageType === "media" && !item.message.deletedAt && !isEngageSpecial(item.message) ? (
																	isEngageVideo(item.message) || isEngageAudio(item.message) ? (
																		<EngagePlayback conversationId={conversationId} message={item.message} />
																	) : isEngageFile(item.message) ? (
																		<EngageFile conversationId={conversationId} message={item.message} />
																	) : item.message.mediaSha256 ? (
																		<EngageMedia
																			conversationId={conversationId}
																			message={item.message}
																			onOpen={() => setViewingId(item.message.id)}
																		/>
																	) : (
																		<span className="team-media-wait">{item.message.body || "Photo"}</span>
																	)
																) : null}
																{item.message.messageType === "media" ? (
																	<>
																		{engageVisibleCaption(item.message.body, item.message.mediaName) ? (
																			<EngageRichText body={item.message.body} entities={item.message.textEntities} />
																		) : null}
																		{item.message.editedAt ? <span className="team-edited">Edited</span> : null}
																		<time dateTime={item.message.createdAt}>
																			{clockTime(item.message.createdAt)}
																			{item.message.sendStatus
																				? ` · ${statusLabel(item.message.sendStatus, conversation?.network)}`
																				: ""}
																		</time>
																	</>
																) : (
																	<span className="team-bubble-main">
																		{item.message.body && !isEngageSpecial(item.message) ? (
																			<EngageRichText body={item.message.body} entities={item.message.textEntities} />
																		) : null}
																		{item.message.editedAt ? <span className="team-edited">Edited</span> : null}
																		<time dateTime={item.message.createdAt}>
																			{clockTime(item.message.createdAt)}
																			{item.message.sendStatus
																				? ` · ${statusLabel(item.message.sendStatus, conversation?.network)}`
																				: ""}
																		</time>
																	</span>
																)}
															</>
														)}
														{item.message.deletedAt ? (
															<time dateTime={item.message.createdAt}>{clockTime(item.message.createdAt)}</time>
														) : null}
													</span>
												</li>
											);
										})}
									</ol>
								)}
								{awayFromLatest ? (
									<button
										className="engage-jump"
										type="button"
										aria-label="Latest messages"
										onClick={() => stickToLatest("smooth")}
									>
										<ArrowDown size={20} aria-hidden="true" />
									</button>
								) : null}
							</div>
							<div className={pendingEdit || pendingReply ? "team-composer is-editing" : "team-composer"}>
								{closed ? <p className="empty">The reply window is closed. Wait for them to write again.</p> : null}
								{accountOffline ? (
									<p className="empty">This account is offline. Reconnect it from Connect before sending.</p>
								) : null}
								{error ? <p className="err">{error}</p> : null}
								{pendingEdit ? (
									<div className="team-edit-bar">
										<span className="team-edit-mark" aria-hidden="true" />
										<div className="team-edit-copy">
											<strong>Editing</strong>
											<span>{pendingEdit.body}</span>
										</div>
										<button className="team-icon-btn" type="button" aria-label="Cancel edit" onClick={cancelEdit}>
											<CloseCircle size={18} aria-hidden="true" />
										</button>
									</div>
								) : pendingReply ? (
									<div className="team-edit-bar is-reply">
										<Reply size={18} aria-hidden="true" />
										<div className="team-edit-copy">
											<strong>Reply to {pendingReply.name}</strong>
											<span>{pendingReply.body}</span>
										</div>
										<button className="team-icon-btn" type="button" aria-label="Cancel reply" onClick={cancelReply}>
											<CloseCircle size={18} aria-hidden="true" />
										</button>
									</div>
								) : null}
								{drafts.length > 0 && !pendingEdit ? (
									<div className="team-draft">
										<div className="team-draft-strip">
											{drafts.map((item) => (
												<span className="team-draft-tile" key={item.id}>
													{item.kind === "image" && item.url ? (
														<img className="team-draft-media" src={item.url} alt="" />
													) : (
														<span className="team-draft-file">
															<Document size={16} aria-hidden="true" />
															{item.file.name}
														</span>
													)}
													<button
														type="button"
														className="team-icon-btn"
														aria-label="Remove attachment"
														onClick={() => removeDraft(item.id)}
													>
														<CloseCircle size={14} aria-hidden="true" />
													</button>
												</span>
											))}
										</div>
									</div>
								) : null}
								{voiceOpen && !pendingEdit ? (
									<EngageVoiceCapture
										disabled={composeBlocked}
										onCancel={() => setVoiceOpen(false)}
										onError={(message) => {
											setVoiceOpen(false);
											setError(message);
										}}
										onRecorded={(file) => {
											setVoiceOpen(false);
											if (composeBlocked) return;
											reply.mutate({
												text: "",
												files: [{ file }],
												replyToMessageId: pendingReply?.id,
												replyName: pendingReply?.name,
												replyBody: pendingReply?.body,
											});
										}}
									/>
								) : (
									<form
										className="team-compose"
										onSubmit={(event) => {
											event.preventDefault();
											if (pendingEdit) {
												if (body.trim()) editMsg.mutate({ id: pendingEdit.id, text: body.trim() });
												return;
											}
											if ((body.trim() || drafts.length) && !composeBlocked) {
												reply.mutate({
													text: body.trim(),
													files: drafts.map((draft) => ({ file: draft.file })),
													replyToMessageId: pendingReply?.id,
													replyName: pendingReply?.name,
													replyBody: pendingReply?.body,
												});
											}
										}}
									>
										<button
											className="team-icon-btn"
											type="button"
											aria-label="Attach"
											aria-expanded={attachOpen}
											disabled={composeBlocked || Boolean(pendingEdit)}
											onClick={() => setAttachOpen((open) => !open)}
										>
											<Paperclip size={18} aria-hidden="true" />
										</button>
										<label className="sr" htmlFor="engage-reply">
											Message
										</label>
										<input
											id="engage-reply"
											value={body}
											onChange={(event) => setBody(event.target.value)}
											onFocus={() => {
												requestAnimationFrame(() => {
													if (pinnedToLatest.current) stickToLatest();
												});
											}}
											onKeyDown={(event) => {
												if (event.key === "Escape" && pendingEdit) {
													event.preventDefault();
													cancelEdit();
												}
												if (event.key === "Escape" && pendingReply) {
													event.preventDefault();
													cancelReply();
												}
											}}
											placeholder="Message"
											maxLength={4000}
											disabled={composeBlocked}
											autoComplete="off"
										/>
										{pendingEdit ? null : (
											<EngageVoiceMic
												disabled={composeBlocked}
												onStart={() => {
													setAttachOpen(false);
													setVoiceOpen(true);
												}}
											/>
										)}
										<button
											className="team-send"
											type="submit"
											disabled={
												pendingEdit
													? editMsg.isPending || !body.trim()
													: composeBlocked || (!body.trim() && !drafts.length)
											}
											aria-label={pendingEdit ? "Save edit" : "Send"}
										>
											{pendingEdit ? <TickCircle size={20} aria-hidden="true" /> : <Send2 size={18} aria-hidden="true" />}
										</button>
									</form>
								)}
								{attachOpen && !voiceOpen ? (
									<div className="team-attach-menu" role="menu" onPointerDown={(event) => event.stopPropagation()}>
										<label className="team-attach-pick" role="menuitem">
											<input
												ref={fileRef}
												className="team-file"
												type="file"
												accept="image/jpeg,image/png,image/webp,image/gif"
												multiple
												onChange={(event) => {
													pickFiles(event.target.files, true);
													event.target.value = "";
												}}
											/>
											<Gallery size={18} aria-hidden="true" />
											Photo
										</label>
										<label className="team-attach-pick" role="menuitem">
											<input
												className="team-file"
												type="file"
												multiple
												onChange={(event) => {
													pickFiles(event.target.files);
													event.target.value = "";
												}}
											/>
											<Document size={18} aria-hidden="true" />
											File
										</label>
									</div>
								) : null}
							</div>
						</div>
						{msgMenu ? (
							<div
								className="team-row-menu"
								role="menu"
								aria-label="Message"
								style={{ top: msgMenu.y, left: msgMenu.x }}
								onPointerDown={(event) => event.stopPropagation()}
							>
								<button
									type="button"
									role="menuitem"
									onClick={() => startReply(msgMenu.id, msgMenu.quoteName, msgMenu.body)}
								>
									Reply
								</button>
								{msgMenu.canEdit ? (
									<button type="button" role="menuitem" onClick={() => startEdit(msgMenu.id, msgMenu.body)}>
										Edit
									</button>
								) : null}
								{msgMenu.canDelete ? (
									<button
										className="danger"
										type="button"
										role="menuitem"
										onClick={() => {
											setPendingMsgDelete(msgMenu.id);
											setMsgMenu(null);
										}}
									>
										Delete
									</button>
								) : null}
							</div>
						) : null}
						<Popup
							open={Boolean(pendingMsgDelete)}
							title="Delete message"
							onClose={() => setPendingMsgDelete(null)}
						>
							<p className="sub">This message will be deleted for everyone in the chat.</p>
							<div className="form-actions">
								<button className="btn ghost" type="button" onClick={() => setPendingMsgDelete(null)}>
									Cancel
								</button>
								<button
									className="btn"
									type="button"
									disabled={deleteMsg.isPending}
									onClick={() => {
										const id = pendingMsgDelete;
										if (!id) return;
										setPendingMsgDelete(null);
										const listId = messages.data?.length ? engageListItemIdForMessage(messages.data, id) : id;
										window.requestAnimationFrame(() => {
											const row =
												document.getElementById(`engage-msg-${id}`) ??
												document.getElementById(`engage-msg-${listId}`);
											dissolveElement(row, {
												onDone: () => {
													setVanished((current) => (current.includes(id) ? current : [...current, id]));
													deleteMsg.mutate(id);
												},
											});
										});
									}}
								>
									{deleteMsg.isPending ? "Deleting" : "Delete"}
								</button>
							</div>
						</Popup>
						{viewingId ? (
							<EngageMediaViewer
								conversationId={conversationId}
								items={(messages.data ?? []).filter((row) => isEngagePhoto(row))}
								currentId={viewingId}
								onClose={() => setViewingId("")}
								onSelect={setViewingId}
							/>
						) : null}
						<Popup open={membersOpen} title={conversationTitle(conversation)} onClose={() => setMembersOpen(false)}>
							{groupPeople.length ? (
								<ul className="team-member-list">
									{groupPeople.map((person) => {
										const name = participantLabel(person, conversation?.network);
										const detail = participantNumber(person, conversation?.network);
										return (
											<li key={person.providerId}>
												<span className={`team-avatar tone-${toneOf(name)}`} aria-hidden="true">
													{initials(name)}
												</span>
												<span className="team-row-main">
													<strong>{name}</strong>
													{detail && detail !== name ? <span>{detail}</span> : null}
												</span>
											</li>
										);
									})}
								</ul>
							) : (
								<p className="empty">Members appear when the group list arrives.</p>
							)}
						</Popup>
					</>
				) : (
					<p className="empty team-pick">Open a chat.</p>
				)}
			</section>
			{chatMenu ? (
				<div
					className="team-row-menu"
					role="menu"
					aria-label="Chat"
					style={{ top: chatMenu.y, left: chatMenu.x }}
					onPointerDown={(event) => event.stopPropagation()}
				>
					<button
						type="button"
						role="menuitem"
						onClick={() => pinChat.mutate({ id: chatMenu.id, pinned: !chatMenu.pinned })}
					>
						{chatMenu.pinned ? "Unpin" : "Pin"}
					</button>
					<button
						className="danger"
						type="button"
						role="menuitem"
						onClick={() => {
							setPendingChatDelete({ id: chatMenu.id, name: chatMenu.name });
							setChatMenu(null);
						}}
					>
						Delete
					</button>
				</div>
			) : null}
			<Popup open={Boolean(pendingChatDelete)} title="Delete chat" onClose={() => setPendingChatDelete(null)}>
				<p className="sub">This chat will be removed from the inbox. A new message will bring it back.</p>
				<div className="form-actions">
					<button className="btn ghost" type="button" onClick={() => setPendingChatDelete(null)}>
						Cancel
					</button>
					<button
						className="btn"
						type="button"
						disabled={deleteChat.isPending}
						onClick={() => {
							const target = pendingChatDelete;
							if (!target) return;
							setPendingChatDelete(null);
							window.requestAnimationFrame(() => {
								const row = document.querySelector(`[href$="/engage/inbox/${target.id}"]`)?.closest("li");
								dissolveElement(row instanceof HTMLElement ? row : null, {
									onDone: () => deleteChat.mutate(target.id),
								});
							});
						}}
					>
						{deleteChat.isPending ? "Deleting" : "Delete"}
					</button>
				</div>
			</Popup>
		</div>
	);
}

export function EngageAccounts() {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const query = useQuery({
		queryKey: queryKeys.engageAccounts,
		queryFn: () => api.listEngageAccounts(),
		refetchInterval: visibilityInterval,
	});
	const catalog = useQuery({ queryKey: queryKeys.engageCatalog, queryFn: () => api.listEngageCatalog() });
	const queryClient = useQueryClient();
	const [find, setFind] = useState("");
	const [open, setOpen] = useState<EngageInboxNetwork | null>(null);
	const [loginId, setLoginId] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [consent, setConsent] = useState(false);
	const [nickname, setNickname] = useState("");
	const [cloudPassword, setCloudPassword] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [loginCode, setLoginCode] = useState("");
	const [telegramMethod, setTelegramMethod] = useState<"qr" | "phone_code">("qr");
	const [linkedinEmail, setLinkedinEmail] = useState("");
	const [linkedinPassword, setLinkedinPassword] = useState("");
	const finishing = useRef(false);
	const auth = useSession();
	const organisationId = auth.data?.activeOrganisationId;

	useEffect(() => {
		if (!organisationId || typeof window === "undefined") return;
		const protocol = window.location.protocol === "https:" ? "wss" : "ws";
		const socket = new WebSocket(
			`${protocol}://${window.location.host}/api/v1/engage/ws?organisationId=${encodeURIComponent(organisationId)}`,
		);
		socket.onmessage = (event) => applyEngageLiveFrame(queryClient, String(event.data));
		return () => socket.close();
	}, [organisationId, queryClient]);

	useEffect(() => {
		if (params.get("error") === "login") {
			setError(params.get("reason")?.trim() || "That login did not finish.");
		}
	}, [params]);

	const login = useQuery({
		queryKey: queryKeys.engageLogin(loginId),
		queryFn: () => api.getEngageLogin(loginId),
		enabled: Boolean(loginId),
		refetchInterval: loginId ? 2000 : false,
	});

	const loginPhone = normalizeEngageLoginPhone(phoneNumber);
	const phoneHint = engageLoginPhoneHint(phoneNumber);

	const start = useMutation({
		mutationFn: (input: EngageInboxNetwork | { network: EngageInboxNetwork; oauth: true }) => {
			const network = typeof input === "string" ? input : input.network;
			if (typeof input !== "string" && input.oauth) {
				return api.startEngageLogin(network, {
					nickname: nickname.trim() || undefined,
					method: "oauth",
				});
			}
			return api.startEngageLogin(network, {
				nickname: nickname.trim() || undefined,
				consentAcknowledged: consent || undefined,
				...(network === "telegram" && telegramMethod === "phone_code"
					? { method: "phone_code", phone: loginPhone ?? undefined }
					: {}),
				...(network === "linkedin"
					? { method: "email", email: linkedinEmail.trim() || undefined }
					: {}),
			});
		},
		onSuccess: async (session) => {
			setOpen(session.network);
			setLoginId(session.id);
			if (session.method === "oauth" && session.loginUrl) {
				window.location.assign(session.loginUrl);
				return;
			}
			if (session.network === "linkedin" && linkedinPassword.trim()) {
				try {
					const next = await api.submitEngageLoginSecret(session.id, { password: linkedinPassword });
					queryClient.setQueryData(queryKeys.engageLogin(session.id), next);
				} catch (caught) {
					setError(presentError(caught).title);
				}
			}
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const connect = useMutation({
		mutationFn: () => api.completeEngageLogin(loginId),
		onSuccess: async () => {
			setOpen(null);
			setLoginId("");
			setConsent(false);
			await queryClient.invalidateQueries();
			navigate("/engage/inbox");
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const refresh = useMutation({
		mutationFn: () => api.refreshEngageLogin(loginId),
		onSuccess: (next) => {
			queryClient.setQueryData(queryKeys.engageLogin(next.id), next);
			if (next.id !== loginId) {
				queryClient.removeQueries({ queryKey: queryKeys.engageLogin(loginId) });
			}
			setLoginId(next.id);
			setError(null);
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const submitSecret = useMutation({
		mutationFn: () =>
			api.submitEngageLoginSecret(
				loginId,
				open === "linkedin"
					? login.data?.status === "code"
						? { code: loginCode.trim() }
						: { password: linkedinPassword }
					: login.data?.status === "pending"
						? { phone: loginPhone ?? phoneNumber.trim() }
						: loginCode.trim() && !cloudPassword
							? { code: loginCode.trim() }
							: { password: cloudPassword },
			),
		onSuccess: () => {
			setCloudPassword("");
			setLoginCode("");
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	const cancel = useMutation({
		mutationFn: () => api.cancelEngageLogin(loginId),
		onSuccess: () => setLoginId(""),
	});

	useEffect(() => {
		if (login.data?.status !== "connected" || !loginId || finishing.current) return;
		finishing.current = true;
		connect.mutate(undefined, { onSettled: () => { finishing.current = false; } });
	}, [connect, login.data?.status, loginId]);

	const disconnect = useMutation({
		mutationFn: (id: string) => api.disconnectEngageAccount(id),
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: queryKeys.engageAccounts });
			const previous = queryClient.getQueryData(queryKeys.engageAccounts);
			const clicked = (previous as EngageAccountDto[] | undefined)?.find((row) => row.id === id);
			const phone =
				clicked?.network === "whatsapp" ? engageWhatsAppAccountKey(clicked.providerAccountId) : "";
			queryClient.setQueryData(queryKeys.engageAccounts, (rows: EngageAccountDto[] | undefined) =>
				(rows ?? []).filter((row) => {
					if (row.id === id) return false;
					return !(
						phone &&
						row.network === "whatsapp" &&
						engageWhatsAppAccountKey(row.providerAccountId) === phone
					);
				}),
			);
			return { previous };
		},
		onError: (caught, _id, context) => {
			if (context?.previous) queryClient.setQueryData(queryKeys.engageAccounts, context.previous);
			setError(presentError(caught).title);
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageAccounts });
			await queryClient.invalidateQueries({ queryKey: queryKeys.engageConversations });
		},
	});

	function pick(network: EngageInboxNetwork) {
		setError(null);
		setLoginId("");
		setConsent(false);
		setOpen(network);
	}

	const session = login.data?.network === open ? login.data : start.data?.network === open ? start.data : undefined;

	useEffect(() => {
		if (open !== "linkedin" || !loginId) return;
		if (session?.status === "qr" && session.loginUrl === "linkedin-challenge") {
			navigate(`/engage/linkedin-sign-in/${loginId}`, { replace: true });
		}
	}, [loginId, navigate, open, session?.loginUrl, session?.status]);

	const linkedinEnded =
		open === "linkedin" && Boolean(loginId) && (session?.status === "failed" || session?.status === "expired");
	const qrValue = session?.qrValue ?? "";
	const loginUrl = session?.loginUrl ?? "";
	const method = session?.method ?? capabilityOf(catalog.data, open)?.pairing ?? "qr";
	const capability = capabilityOf(catalog.data, open);
	const needle = find.trim().toLowerCase();
	const grid = ENGAGE_CONNECT_NETWORKS.filter((network) => connectLabel(network).toLowerCase().includes(needle));
	const accounts = open ? (query.data ?? []).filter((item) => item.network === open) : [];
	const workspaceLinked = accounts.some((account) => account.status === "active");
	const linkedinSyncing =
		open === "linkedin" && workspaceLinked && !accounts.some((account) => account.lastSyncAt);
	const pairingCollapsed = workspaceLinked && !linkedinSyncing && !loginId;
	const PairingWrap = pairingCollapsed ? "details" : "div";
	const pairingWrapClass = pairingCollapsed ? "engage-link-expand" : "engage-link-block";

	if (query.isPending) return <p className="empty">Loading accounts.</p>;
	if (query.isError) return <p className="err">{presentError(query.error).title}</p>;

	return (
		<div className={open ? "engage-connect-app is-link" : "engage-connect-app"}>
			<section className="engage-connect" aria-label="Connect chat accounts">
				<header className="engage-connect-head">
					<button
						type="button"
						className="engage-link-back"
						aria-label="Open menu"
						onClick={() => window.dispatchEvent(new Event("bafe:open-nav"))}
					>
						<Menu size={22} aria-hidden="true" />
					</button>
					<h1>Connect Chat Accounts</h1>
				</header>
				<label className="engage-connect-search">
					<span className="sr">Search</span>
					<Search size={16} aria-hidden="true" />
					<input value={find} onChange={(event) => setFind(event.target.value)} placeholder="Search..." autoComplete="off" />
				</label>
				{error && <p className="err">{error}</p>}
				<div className="engage-connect-grid">
					{grid.map((network) => {
						const item = capabilityOf(catalog.data, network);
						const networkAccounts = (query.data ?? []).filter((account) => account.network === network);
						const linked = networkAccounts.some((account) => account.status === "active");
						const gone =
							!linked &&
							networkAccounts.some((account) => account.status === "expired" || account.status === "disconnected");
						const offline = !linked && !gone && networkAccounts.some((account) => account.status === "degraded");
						const tileStatus = connectTileStatus(network, linked, gone, offline, networkAccounts, item);
						return (
							<button
								type="button"
								className={open === network ? "engage-connect-app-btn is-on" : "engage-connect-app-btn"}
								key={network}
								aria-label={tileStatus ? `${connectLabel(network)} · ${tileStatus}` : connectLabel(network)}
								onClick={() => pick(network)}
							>
								<EngageNetworkIcon network={network} />
								<span>
									{connectLabel(network)}
									{tileStatus ? ` · ${tileStatus}` : ""}
								</span>
							</button>
						);
					})}
				</div>
				{!grid.length ? <p className="empty">No apps match that search.</p> : null}
			</section>
			{open && capability ? (
				<section
					className={workspaceLinked ? "engage-link is-connected" : "engage-link"}
					aria-label={`Add ${connectLabel(open)}`}
				>
					<header className="engage-link-head">
						<button
							type="button"
							className="engage-link-back"
							aria-label="Back to apps"
							onClick={() => {
								setOpen(null);
								setLoginId("");
								setError(null);
							}}
						>
							<ChevronLeft size={22} aria-hidden="true" />
						</button>
						<EngageNetworkIcon network={open} size={28} />
						<h2>{connectLabel(open)}</h2>
					</header>
					<div className="engage-link-body">
						{workspaceLinked ? (
							<div className="engage-link-connected">
								<div className="engage-link-connected-top">
									<p
										className={linkedinSyncing ? "engage-link-chip is-warning" : "engage-link-chip"}
									>
										{connectedChipLabel(open, linkedinSyncing)}
									</p>
									{!linkedinSyncing && inboxReadyNetwork(open) ? (
										<Link className="btn engage-link-go" to="/engage/inbox">
											Open inbox
										</Link>
									) : null}
								</div>
								<p className="engage-link-title">{connectedSummaryCopy(open, linkedinSyncing)}</p>
								{linkedinSyncing ? (
									<p className="engage-link-note engage-link-workspace">
										{connectedWorkspaceNote(open, true)}
									</p>
								) : null}
							</div>
						) : (
							<>
								<p className="engage-link-title">{linkSummaryCopy(open, capability.summary)}</p>
								<p className="engage-link-note engage-link-status">
									{linkStatusCopy(open, capability.status, capability.blocker)}
								</p>
							</>
						)}
						{accounts.length ? (
							<div className="engage-link-block">
								{!workspaceLinked ? <h3>{connectedAccountsHeading(accounts)}</h3> : null}
								<ul className="engage-account-list">
									{accounts.map((account) => (
										<li key={account.id}>
											<div className="engage-account-main">
												<strong>{account.nickname || account.displayName}</strong>
												<span>{accountSyncLabel(open, account)}</span>
											</div>
											{engageAccountShowsDisconnect(account, auth.data?.user.id) ? (
												<button
													className="btn ghost"
													type="button"
													disabled={disconnect.isPending && disconnect.variables === account.id}
													onClick={() => disconnect.mutate(account.id)}
												>
													{disconnect.isPending && disconnect.variables === account.id
														? "Disconnecting"
														: "Disconnect"}
												</button>
											) : null}
										</li>
									))}
								</ul>
							</div>
						) : null}
						{capability.status === "WEB_ONLY" || capability.status === "BLOCKED" ? (
							<div className="engage-link-block">
								<p className="engage-link-note">
									{capability.blocker} Opening the official site is not a Key Engage inbox.
								</p>
								{capability.webInboxUrl ? (
									<a className="btn engage-link-go" href={capability.webInboxUrl} target="_blank" rel="noreferrer">
										Open official web inbox
									</a>
								) : null}
							</div>
						) : capability.status === "PARTIAL" && capability.webInboxUrl ? (
							<div className="engage-link-block">
								<p className="engage-link-note">{statusCopy(capability.status, open)}</p>
								<a className="btn ghost engage-link-go" href={capability.webInboxUrl} target="_blank" rel="noreferrer">
									Open official web inbox
								</a>
							</div>
						) : null}
						{capability.status !== "WEB_ONLY" && capability.status !== "BLOCKED" ? (
							<PairingWrap className={pairingWrapClass}>
								{pairingCollapsed ? (
									<summary>{pairingExpandLabel(open)}</summary>
								) : null}
								{!pairingCollapsed ? (
									<h3>
										{open === "linkedin"
											? accounts.some((account) => account.status === "active")
												? "Connect again"
												: "Connect LinkedIn"
											: accounts.some((account) => account.status === "active")
												? "Add another account"
												: "Link a device"}
									</h3>
								) : null}
								<div className="engage-link-pairing">
								<ol className="engage-link-steps">
									{linkSteps(open, telegramMethod).map((step, index) => (
										<li key={step}>
											<span>{index + 1}</span>
											<p>{step}</p>
										</li>
									))}
								</ol>
								<label className="engage-nickname">
									<span className="sr">Nickname</span>
									<input
										value={nickname}
										onChange={(event) => setNickname(event.target.value)}
										placeholder="Nickname (optional)"
										maxLength={80}
									/>
								</label>
								{capability.experimental ? (
									<label className="engage-consent">
										<input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
										<span>
											{open === "linkedin"
												? "I understand this unofficial LinkedIn session can be disconnected, restricted, or locked by LinkedIn."
												: "I understand this unofficial WhatsApp Web connection can be disconnected, restricted, or banned by WhatsApp, and I may pair only an approved test account."}
										</span>
									</label>
								) : null}
								{open === "linkedin" && (!loginId || session?.status === "pending") ? (
									<>
										<label className="engage-nickname is-labeled">
											<span>LinkedIn email</span>
											<input
												type="email"
												value={linkedinEmail}
												onChange={(event) => setLinkedinEmail(event.target.value)}
												placeholder="name@company.com"
												autoComplete="username"
												aria-label="LinkedIn email"
											/>
										</label>
										<label className="engage-nickname is-labeled">
											<span>LinkedIn password</span>
											<input
												type="password"
												value={linkedinPassword}
												onChange={(event) => setLinkedinPassword(event.target.value)}
												placeholder="Password"
												autoComplete="current-password"
												aria-label="LinkedIn password"
											/>
										</label>
									</>
								) : null}
								{open === "telegram" && !loginId ? (
									<fieldset className="engage-method">
										<legend>Sign in with</legend>
										<div className="engage-method-options" role="presentation">
											<label className={telegramMethod === "qr" ? "is-on" : undefined}>
												<input
													type="radio"
													name="telegram-method"
													checked={telegramMethod === "qr"}
													onChange={() => setTelegramMethod("qr")}
												/>
												<span>QR code</span>
											</label>
											<label className={telegramMethod === "phone_code" ? "is-on" : undefined}>
												<input
													type="radio"
													name="telegram-method"
													checked={telegramMethod === "phone_code"}
													onChange={() => setTelegramMethod("phone_code")}
												/>
												<span>Phone number</span>
											</label>
										</div>
										<p className="engage-method-hint">
											{telegramMethod === "qr"
												? "Scan from Telegram → Settings → Devices on the company phone."
												: "Telegram sends a login code to this number. Use country code, like +60."}
										</p>
									</fieldset>
								) : null}
								{(open === "telegram" && telegramMethod === "phone_code" && !loginId) ||
								(open !== "linkedin" && session?.status === "pending") ? (
									<label className="engage-nickname is-labeled">
										<span>Telegram number</span>
										<input
											type="tel"
											inputMode="tel"
											value={phoneNumber}
											onChange={(event) => setPhoneNumber(event.target.value)}
											placeholder="+60 12 345 6789"
											autoComplete="tel"
											aria-label="Telegram number"
											aria-invalid={Boolean(phoneNumber.trim()) && !loginPhone}
											aria-describedby="engage-phone-hint"
										/>
										<span
											id="engage-phone-hint"
											className={phoneNumber.trim() && !loginPhone ? "engage-field-hint is-bad" : "engage-field-hint"}
										>
											{phoneNumber.trim() && !loginPhone
												? phoneHint
												: loginPhone
													? `We'll send the login code to ${loginPhone}.`
													: "Include the country code. Example: +60 12 345 6789."}
										</span>
									</label>
								) : null}
								{qrValue && method === "qr" && session?.status === "qr" ? (
									<div className="engage-qr">
										<QrCode value={qrValue} label={`${connectLabel(open)} Linked Devices QR code`} size={196} />
										<span className="engage-qr-mark">
											<EngageNetworkIcon network={open} size={40} />
										</span>
									</div>
								) : null}
								{loginId && method === "qr" && session?.status === "qr" && !qrValue && !session?.hint ? (
									<p className="engage-link-note">Signing in on the phone. Keep this page open.</p>
								) : null}
								{session?.hint ? <p className="engage-link-note">{session.hint}</p> : null}
								{session?.status === "code" && (open === "linkedin" || session.hint?.toLowerCase().includes("login code")) ? (
									<label className="engage-nickname is-labeled">
										<span>{open === "linkedin" ? "Verification code" : "Telegram login code"}</span>
										<input
											value={loginCode}
											onChange={(event) => setLoginCode(event.target.value)}
											placeholder={open === "linkedin" ? "Code from email, SMS, or LinkedIn" : "Login code"}
											autoComplete="one-time-code"
											inputMode="numeric"
											aria-label={open === "linkedin" ? "LinkedIn verification code" : "Telegram login code"}
										/>
									</label>
								) : null}
								{session?.status === "code" && open !== "linkedin" ? (
									<label className="engage-nickname">
										<span className="sr">Telegram cloud password</span>
										<input
											type="password"
											value={cloudPassword}
											onChange={(event) => setCloudPassword(event.target.value)}
											placeholder="Telegram cloud password"
											autoComplete="current-password"
										/>
									</label>
								) : null}
								{session?.expiresAt ? (
									<EngageLoginExpiry expiresAt={session.expiresAt} method={method} />
								) : null}
								{method === "oauth" && loginUrl ? (
									<a className="btn engage-link-go" href={loginUrl}>
										Continue with {connectLabel(open)}
									</a>
								) : (
									<div className="engage-link-actions">
										<button
											className="btn engage-link-go"
											type="button"
											onClick={() => {
												if (linkedinEnded) {
													setError(null);
													cancel.mutate();
													return;
												}
												if (open === "linkedin") {
													if (!linkedinEmail.trim() || !linkedinEmail.includes("@")) {
														setError("Enter your LinkedIn email.");
														return;
													}
													if (!loginId && !linkedinPassword.trim()) {
														setError("Enter your LinkedIn password.");
														return;
													}
												}
												if (
													open !== "linkedin" &&
													((open === "telegram" && telegramMethod === "phone_code" && !loginId) ||
														session?.status === "pending") &&
													!loginPhone
												) {
													setError(phoneHint);
													return;
												}
												if (session?.status === "code" || (open !== "linkedin" && session?.status === "pending")) {
													submitSecret.mutate();
													return;
												}
												if (loginId) {
													connect.mutate();
													return;
												}
												start.mutate(open);
											}}
											disabled={
												!linkedinEnded && (start.isPending ||
												connect.isPending ||
												submitSecret.isPending ||
												(capability.experimental && !consent) ||
												(open === "linkedin" && !loginId && (!linkedinEmail.trim() || !linkedinPassword.trim())) ||
												(open === "linkedin" && session?.status === "code" && !loginCode) ||
												(open === "linkedin" && Boolean(loginId) && session?.status === "pending") ||
												(open !== "linkedin" && session?.status === "code" && !cloudPassword && !loginCode) ||
												(open === "telegram" && session?.status === "pending" && !loginPhone) ||
												(open === "telegram" && telegramMethod === "phone_code" && !loginId && !loginPhone) ||
												(Boolean(loginId) &&
													session?.status !== "connected" &&
													session?.status !== "code" &&
													session?.status !== "pending" &&
													session?.loginUrl !== "linkedin-challenge"))
											}
										>
											{linkedinEnded
												? "Start again"
												: connect.isPending || submitSecret.isPending || session?.status === "connected"
												? "Signing in"
												: session?.status === "pending"
													? open === "linkedin"
														? "Signing in"
														: "Submit phone"
													: session?.status === "code"
														? "Submit code"
														: session?.loginUrl === "linkedin-challenge"
															? "Waiting for check"
															: loginId && !qrValue
																? "Signing in"
																: loginId
																	? "Waiting for phone"
																	: "Connect"}
										</button>
										{loginId && method === "qr" ? (
											<button
												className="btn ghost"
												type="button"
												onClick={() => refresh.mutate()}
												disabled={refresh.isPending}
											>
												{refresh.isPending ? "Refreshing…" : "Refresh QR"}
											</button>
										) : null}
										{loginId ? (
											<button className="btn ghost" type="button" onClick={() => cancel.mutate()}>
												Cancel
											</button>
										) : null}
									</div>
								)}
								</div>
							</PairingWrap>
						) : null}
					</div>
				</section>
			) : null}
		</div>
	);
}

const LINKEDIN_REMOTE_VIEW = { width: 1280, height: 800 } as const;

function linkedInRemoteCoords(
	clientX: number,
	clientY: number,
	box: DOMRect,
	naturalWidth: number,
	naturalHeight: number,
) {
	const scaleX = naturalWidth / Math.max(1, box.width);
	const scaleY = naturalHeight / Math.max(1, box.height);
	return {
		x: Math.round(Math.min(naturalWidth - 1, Math.max(0, (clientX - box.left) * scaleX))),
		y: Math.round(Math.min(naturalHeight - 1, Math.max(0, (clientY - box.top) * scaleY))),
	};
}

export function EngageLinkedInSignIn() {
	const { loginId = "" } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const finishing = useRef(false);
	const [loginCode, setLoginCode] = useState("");

	const login = useQuery({
		queryKey: queryKeys.engageLogin(loginId),
		queryFn: () => api.getEngageLogin(loginId),
		enabled: Boolean(loginId),
		refetchInterval: loginId ? 2000 : false,
	});

	const connect = useMutation({
		mutationFn: () => api.completeEngageLogin(loginId),
		onSuccess: async () => {
			await queryClient.invalidateQueries();
			navigate("/engage/inbox", { replace: true });
		},
	});

	const submitCode = useMutation({
		mutationFn: () => api.submitEngageLoginSecret(loginId, { code: loginCode.trim() }),
		onSuccess: (next) => {
			setLoginCode("");
			queryClient.setQueryData(queryKeys.engageLogin(loginId), next);
		},
	});

	const cancel = useMutation({
		mutationFn: () => api.cancelEngageLogin(loginId),
		onSuccess: () => navigate("/engage/accounts", { replace: true }),
		onError: () => navigate("/engage/accounts", { replace: true }),
	});

	useEffect(() => {
		if (login.data?.status !== "connected" || !loginId || finishing.current) return;
		finishing.current = true;
		connect.mutate(undefined, { onSettled: () => { finishing.current = false; } });
	}, [connect, login.data?.status, loginId]);

	useEffect(() => {
		const status = login.data?.status;
		if (status !== "failed" && status !== "expired") return;
		const reason = login.data?.hint?.trim() || "That LinkedIn sign-in ended.";
		navigate(`/engage/accounts?error=login&reason=${encodeURIComponent(reason)}`, { replace: true });
	}, [login.data?.hint, login.data?.status, navigate]);

	useEffect(() => {
		if (!login.isError) return;
		navigate("/engage/accounts?error=login&reason=That%20LinkedIn%20sign-in%20ended.", { replace: true });
	}, [login.isError, navigate]);

	const live = login.data?.status === "qr" && login.data.loginUrl === "linkedin-challenge";
	const needsCode = login.data?.status === "code";
	const opening = login.data?.status === "pending" || login.data?.status === "connected";

	return (
		<div className="engage-linkedin-signin">
			<header className="engage-linkedin-signin-bar">
				<h1>LinkedIn</h1>
				<button className="btn ghost" type="button" onClick={() => cancel.mutate()}>
					Cancel
				</button>
			</header>
			{live ? (
				<LinkedInRemoteSurface loginId={loginId} />
			) : needsCode ? (
				<form
					className="engage-linkedin-signin-code"
					onSubmit={(event) => {
						event.preventDefault();
						if (loginCode.trim()) submitCode.mutate();
					}}
				>
					<p className="engage-link-note">{login.data?.hint || "Enter the code LinkedIn sent."}</p>
					<label className="engage-nickname is-labeled">
						<span>Verification code</span>
						<input
							value={loginCode}
							onChange={(event) => setLoginCode(event.target.value)}
							placeholder="Code from email, SMS, or LinkedIn"
							autoComplete="one-time-code"
							inputMode="numeric"
							aria-label="LinkedIn verification code"
						/>
					</label>
					<button className="btn engage-link-go" type="submit" disabled={!loginCode.trim() || submitCode.isPending}>
						{submitCode.isPending ? "Submitting" : "Submit code"}
					</button>
				</form>
			) : (
				<p className="engage-link-note engage-linkedin-signin-wait">
					{opening
						? login.data?.hint || "Signed in. Opening your inbox…"
						: login.data?.hint || "Opening LinkedIn…"}
				</p>
			)}
		</div>
	);
}

function LinkedInRemoteSurface({ loginId }: { loginId: string }) {
	const queryClient = useQueryClient();
	const viewportRef = useRef<HTMLDivElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const typeBuffer = useRef("");
	const queue = useRef<LinkedInRemoteInput[]>([]);
	const draining = useRef(false);
	const touch = useRef<{ x: number; y: number; moved: boolean } | null>(null);
	const [failed, setFailed] = useState<string | null>(null);
	const shot = useQuery({
		queryKey: queryKeys.engageLoginChallenge(loginId),
		queryFn: async () => {
			const blob = await api.getEngageLoginChallenge(loginId);
			return URL.createObjectURL(blob);
		},
		refetchInterval: 400,
	});
	const kept = useRef<string | null>(null);
	useEffect(() => {
		if (shot.data && kept.current !== shot.data) {
			if (kept.current) URL.revokeObjectURL(kept.current);
			kept.current = shot.data;
		}
		return () => {
			if (kept.current) URL.revokeObjectURL(kept.current);
			kept.current = null;
		};
	}, [shot.data]);
	const hasShot = Boolean(shot.data);
	useEffect(() => {
		if (hasShot) viewportRef.current?.focus();
	}, [hasShot]);

	// Inputs must reach LinkedIn in the order they were made, so they run one at
	// a time. Consecutive keystrokes and wheel ticks merge into one request.
	const drain = async () => {
		if (draining.current) return;
		draining.current = true;
		try {
			while (queue.current.length) {
				const next = queue.current.shift();
				if (!next) break;
				const login = await api.interactEngageLoginChallenge(loginId, next);
				queryClient.setQueryData(queryKeys.engageLogin(loginId), login);
			}
			setFailed(null);
		} catch (caught) {
			queue.current = [];
			setFailed(presentError(caught).title);
		} finally {
			draining.current = false;
			void queryClient.invalidateQueries({ queryKey: queryKeys.engageLoginChallenge(loginId) });
		}
	};

	const send = (input: LinkedInRemoteInput) => {
		const last = queue.current[queue.current.length - 1];
		if (input.kind === "type" && last?.kind === "type" && last.text.length + input.text.length <= 512) {
			last.text += input.text;
		} else if (input.kind === "scroll" && last?.kind === "scroll") {
			last.deltaX += input.deltaX;
			last.deltaY += input.deltaY;
		} else {
			queue.current.push(input);
		}
		void drain();
	};

	useEffect(() => {
		const element = viewportRef.current;
		if (!element) return;
		const onWheel = (event: WheelEvent) => {
			event.preventDefault();
			send({ kind: "scroll", deltaX: Math.round(event.deltaX), deltaY: Math.round(event.deltaY) });
		};
		element.addEventListener("wheel", onWheel, { passive: false });
		return () => element.removeEventListener("wheel", onWheel);
	});

	const sendClick = (clientX: number, clientY: number) => {
		const img = imgRef.current;
		if (!img) return;
		const box = img.getBoundingClientRect();
		const naturalWidth = img.naturalWidth || LINKEDIN_REMOTE_VIEW.width;
		const naturalHeight = img.naturalHeight || LINKEDIN_REMOTE_VIEW.height;
		const { x, y } = linkedInRemoteCoords(clientX, clientY, box, naturalWidth, naturalHeight);
		send({ kind: "click", x, y });
		viewportRef.current?.focus();
	};

	return (
		<div className="engage-linkedin-remote">
			{!shot.data ? (
				<p className="engage-link-note">
					{shot.isError ? "LinkedIn sign-in could not be shown. Cancel and try Connect again." : "Opening LinkedIn…"}
				</p>
			) : (
				<div
					ref={viewportRef}
					className="engage-linkedin-remote-viewport"
					tabIndex={0}
					role="application"
					aria-label="LinkedIn sign-in"
					aria-describedby="engage-linkedin-remote-help"
					onKeyDown={(event) => {
						if (LINKEDIN_REMOTE_KEYS.has(event.key)) {
							event.preventDefault();
							send({ kind: "key", key: event.key });
							return;
						}
						if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
							event.preventDefault();
							send({ kind: "type", text: event.key });
						}
					}}
					onPaste={(event) => {
						const text = event.clipboardData.getData("text").slice(0, 512);
						if (!text) return;
						event.preventDefault();
						send({ kind: "type", text });
					}}
				>
					<img
						ref={imgRef}
						src={shot.data}
						alt=""
						draggable={false}
						onPointerDown={(event) => {
							if (event.pointerType === "touch") touch.current = { x: event.clientX, y: event.clientY, moved: false };
						}}
						onPointerMove={(event) => {
							const start = touch.current;
							if (!start || event.pointerType !== "touch") return;
							const deltaX = start.x - event.clientX;
							const deltaY = start.y - event.clientY;
							if (!start.moved && Math.hypot(deltaX, deltaY) < 8) return;
							touch.current = { x: event.clientX, y: event.clientY, moved: true };
							send({ kind: "scroll", deltaX: Math.round(deltaX * 2), deltaY: Math.round(deltaY * 2) });
						}}
						onPointerUp={(event) => {
							const moved = touch.current?.moved;
							touch.current = null;
							if (event.button !== 0 || moved) return;
							sendClick(event.clientX, event.clientY);
						}}
					/>
				</div>
			)}
			<p id="engage-linkedin-remote-help" className="engage-link-note">
				Sign in on LinkedIn. After you sign in, this page opens your inbox.
			</p>
			{failed ? (
				<p className="engage-link-note is-bad" role="alert">
					{failed}
				</p>
			) : null}
			<label className="engage-linkedin-remote-type">
				<span>On a phone, type here after you tap a field.</span>
				<input
					type="password"
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="off"
					spellCheck={false}
					aria-label="Type for LinkedIn sign-in"
					onChange={(event) => {
						const value = event.target.value;
						const prior = typeBuffer.current;
						if (value.startsWith(prior) && value.length > prior.length) {
							send({ kind: "type", text: value.slice(prior.length) });
						} else if (prior.startsWith(value)) {
							for (let i = 0; i < prior.length - value.length; i += 1) send({ kind: "key", key: "Backspace" });
						}
						typeBuffer.current = value;
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							send({ kind: "key", key: "Enter" });
							event.currentTarget.value = "";
							typeBuffer.current = "";
						}
					}}
				/>
			</label>
		</div>
	);
}

type LinkedInRemoteInput =
	| { kind: "click"; x: number; y: number }
	| { kind: "type"; text: string }
	| { kind: "key"; key: string }
	| { kind: "scroll"; deltaX: number; deltaY: number };

const LINKEDIN_REMOTE_KEYS = new Set([
	"Enter",
	"Backspace",
	"Tab",
	"Escape",
	"ArrowUp",
	"ArrowDown",
	"ArrowLeft",
	"ArrowRight",
	"Delete",
	"Home",
	"End",
]);

export function EngagePolicy() {
	return (
		<article className="page engage-policy">
			<h1>Engage usage and privacy</h1>
			<p>Key Engage is for Key staff only. Each person connects and manages their own chat accounts.</p>
			<h2>Who can connect accounts</h2>
			<p>Anyone in this workspace can pair WhatsApp, Telegram, or another supported network. Your connection is private to your Key login.</p>
			<h2>What is collected</h2>
			<p>Chats and groups from the accounts you link appear in your Engage inbox, newest first. Groups sit on their own filter. Secret chats and some media stay out.</p>
			<h2>Who can read and reply</h2>
			<p>Only you, for the accounts you connected. Other workspace members cannot open your chats unless they use your Key login.</p>
			<h2>Mistaken or private messages</h2>
			<p>Tell an Engage admin. They can hide the conversation, disconnect the account, and escalate. Do not forward the thread outside Key.</p>
			<h2>Disconnect and incidents</h2>
			<p>Disconnect hides that account in this inbox until the same WhatsApp or Telegram login is connected again. Those chats stay. A different login has its own chats and does not see the first account.</p>
			<p>
				This inbox is not end-to-end encrypted inside Key. Operators and permitted staff can read stored messages.
			</p>
		</article>
	);
}

function capabilityOf(catalog: EngageCatalogItemDto[] | undefined, network: EngageInboxNetwork | null) {
	if (!network) return null;
	const row =
		catalog?.find((item) => item.network === network) ??
		engageNetworkCatalog().find((item) => item.network === network) ??
		null;
	if (!row) return null;
	// Telegram api_id/api_hash are already on the connector. A stale catalog still said setup.
	if (row.network === "telegram" && row.status === "SETUP_REQUIRED") {
		return { ...row, status: "WORKING", blocker: null };
	}
	return row;
}

function connectTileStatus(
	network: EngageInboxNetwork,
	linked: boolean,
	gone: boolean,
	offline: boolean,
	accounts: EngageAccountDto[],
	item: EngageCatalogItemDto | null,
): string {
	if (network === "telegram") return "";
	if (linked) {
		if (network === "linkedin" && !accounts.some((account) => account.lastSyncAt)) {
			return "Syncing";
		}
		return "Connected";
	}
	if (gone) return "Disconnected";
	if (offline) return "Offline";
	return item ? shortStatus(item.status) : "";
}

function shortStatus(status: string): string {
	if (status === "WEB_ONLY") return "Web only";
	if (status === "BLOCKED") return "Unavailable";
	if (status === "SETUP_REQUIRED") return "Setup needed";
	if (status === "PARTIAL") return "Sign-in only";
	if (status === "EXPERIMENTAL") return "";
	if (status === "WORKING") return "Ready";
	return status;
}

function statusCopy(status: string, network?: EngageInboxNetwork): string {
	if (status === "EXPERIMENTAL") {
		if (network === "linkedin") {
			return "LinkedIn messaging in Engage uses your login session. LinkedIn may ask for verification.";
		}
		return "Unofficial WhatsApp Web bridge. Pairing can lead to disconnections, restrictions, or account bans. Use a replaceable test account.";
	}
	if (status === "SETUP_REQUIRED") return "Supported, but operator credentials or platform approval are still missing.";
	if (status === "PARTIAL")
		return "Account can be linked. Messaging on this network is limited.";
	if (status === "WEB_ONLY") return "Not integrated. This opens the network website in a new tab.";
	if (status === "BLOCKED") return "No vetted receive and send transport.";
	if (status === "WORKING") return "Verified in Key Engage.";
	return "Not tested with a live company account yet.";
}

function linkStatusCopy(network: EngageInboxNetwork, status: string, blocker: string | null): string {
	if (status === "WORKING") return "Ready to use in Engage.";
	if (blocker?.trim()) return blocker.trim();
	return statusCopy(status, network);
}

function connectedChipLabel(network: EngageInboxNetwork, linkedinSyncing: boolean): string {
	if (network === "linkedin" && linkedinSyncing) return "Connected · syncing chats";
	return "Connected";
}

function connectedSummaryCopy(network: EngageInboxNetwork, linkedinSyncing: boolean): string {
	if (network === "linkedin" && linkedinSyncing) {
		return "LinkedIn is connected. Your chats are syncing into the inbox.";
	}
	if (network === "linkedin") {
		return "Messages are in your inbox.";
	}
	if (network === "whatsapp") {
		return "Chats are in your inbox.";
	}
	if (network === "telegram") {
		return "Chats are in your inbox.";
	}
	return "This account is connected in Engage.";
}

function connectedWorkspaceNote(network: EngageInboxNetwork, linkedinSyncing: boolean): string {
	if (network === "linkedin") {
		if (linkedinSyncing) {
			return "Keep this page open while the first sync runs. LinkedIn may ask for a code or picture check below.";
		}
		return "Only you can read and reply to this LinkedIn connection in Engage.";
	}
	return "Only you can read and reply to this connection.";
}

function inboxReadyNetwork(network: EngageInboxNetwork): boolean {
	return network === "whatsapp" || network === "telegram" || network === "linkedin";
}

function pairingExpandLabel(network: EngageInboxNetwork): string {
	if (network === "linkedin") return "Connect LinkedIn again";
	return "Add another account";
}

function accountSyncLabel(network: EngageInboxNetwork, account: EngageAccountDto): string {
	const base = accountStatusLabel(account.status);
	if (network === "linkedin" && account.status === "active" && !account.lastSyncAt) {
		return `${base} · Syncing chats`;
	}
	if (account.lastSyncAt) return `${base} · Last synced ${listTime(account.lastSyncAt)}`;
	return base;
}

function linkSummaryCopy(network: EngageInboxNetwork, summary: string): string {
	if (network === "telegram") {
		return "Link your Telegram account. Chats and groups show up in your Engage inbox.";
	}
	if (network === "whatsapp") {
		return "Link WhatsApp from your phone with Linked devices. This is not WhatsApp Business or the Meta Cloud API.";
	}
	if (network === "linkedin") {
		return "Connect LinkedIn once. Your messages sync into Engage and stay connected after restarts.";
	}
	return summary;
}

function connectedAccountsHeading(accounts: EngageAccountDto[]): string {
	if (accounts.some((account) => account.status === "active")) return "You're connected";
	if (accounts.some((account) => account.status === "expired" || account.status === "disconnected")) {
		return "Connection paused";
	}
	return "Not connected yet";
}

function connectLabel(network: string): string {
	if (network === "messenger") return "Facebook Messenger";
	return labelOf(network);
}

function linkSteps(network: string, telegramMethod: "qr" | "phone_code"): string[] {
	if (network === "whatsapp") {
		return [
			"On the phone: WhatsApp → Linked devices → Link a device.",
			"Scan the code shown here. Do not scan a website URL.",
			"Keep this page open. After sign-in, WhatsApp chats appear in the inbox.",
		];
	}
	if (network === "telegram" && telegramMethod === "phone_code") {
		return [
			"Type the Telegram number with country code, for example +60 12 345 6789.",
			"Connect. Telegram sends a login code to that phone — enter the code here.",
			"If two-step verification is on, enter the cloud password after the code.",
		];
	}
	if (network === "telegram") {
		return [
			"Confirm this is a company Telegram user account, not a bot.",
			"Approve the login in Telegram. 2FA stays on the phone.",
			"Chats allowed by policy appear in this inbox.",
		];
	}
	if (network === "messenger") {
		return ["Continue opens Facebook.", "Choose the Page for Messenger.", "This is Page messaging, not a personal account."];
	}
	if (network === "instagram") {
		return ["Continue opens Instagram on Meta.", "Choose the professional account.", "Personal DMs are not this connector."];
	}
	if (network === "linkedin") {
		return [
			"Enter the LinkedIn email and password for this company account.",
			"Connect opens LinkedIn so you can sign in there — including Microsoft or Apple.",
			"After you sign in, Key brings you to the inbox. Keep that page open until chats appear.",
		];
	}
	return ["This network is not a unified-inbox integration yet."];
}

function ConversationRow({
	item,
	active,
	showAccount,
	onContextMenu,
	onPointerDown,
	onPointerUp,
	onClick,
}: {
	item: EngageConversationDto;
	active: boolean;
	showAccount?: boolean;
	onContextMenu: (event: MouseEvent<HTMLAnchorElement>, item: EngageConversationDto) => void;
	onPointerDown: (event: PointerEvent<HTMLAnchorElement>, item: EngageConversationDto) => void;
	onPointerUp: () => void;
	onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
	const title = conversationTitle(item);
	const phone = conversationPhone(item);
	const preview = engageIsPlaceholderBody(item.lastMessage) ? "" : (item.lastMessage ?? "");
	const kindMark = item.kind === "group" ? "Group" : "";
	const account = showAccount ? item.accountName : "";
	const lead = [kindMark, phone && title !== phone ? phone : "", account].filter(Boolean).join(" · ");
	const subtitle = lead && preview ? `${lead} · ${preview}` : lead || preview;
	const unread = unreadBadge(item);
	return (
		<Link
			className={active ? "team-row active" : "team-row"}
			to={`/engage/inbox/${item.id}`}
			onContextMenu={(event) => onContextMenu(event, item)}
			onPointerDown={(event) => onPointerDown(event, item)}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			onClick={onClick}
		>
			<span className={`team-avatar engage-avatar tone-${toneOf(title)}${item.kind === "group" ? " is-group" : ""}`} aria-hidden="true">
				{initials(title)}
				<span className={`engage-net net-${item.network}`}>{networkMark(item.network)}</span>
			</span>
			<span className="team-row-main">
				<strong>{title}</strong>
				<span>{subtitle}</span>
			</span>
			<span className="team-row-meta engage-row-meta">
				{item.lastMessageAt ? (
					<span className="engage-meta-line">
						<time dateTime={item.lastMessageAt}>{listTime(item.lastMessageAt)}</time>
					</span>
				) : null}
				{item.pinned || (!active && unread) ? (
					<span className="engage-meta-foot">
						{!active && unread ? (
							<span className="engage-unread" aria-label={`${unread} unread`}>
								{unread}
							</span>
						) : null}
						{item.pinned ? (
							<span className="engage-pin" aria-label="Pinned">
								<EngagePinIcon />
							</span>
						) : null}
					</span>
				) : null}
			</span>
		</Link>
	);
}

function unreadBadge(item: EngageConversationDto): string {
	const count = item.unreadCount ?? (item.unread ? 1 : 0);
	if (count <= 0) return "";
	return count > 99 ? "99+" : String(count);
}

function conversationTitle(item: EngageConversationDto): string {
	return (
		engageConversationTitle({
			customerName: item.customerName,
			customerHandle: item.customerHandle,
			kind: item.kind === "group" ? "group" : "dm",
		}) || (item.kind === "group" ? "Group" : conversationPhone(item))
	);
}

function conversationPhone(item: EngageConversationDto): string {
	if (item.network !== "whatsapp" || item.kind === "group") return "";
	return (
		engageWhatsAppPhone(item.customerPhone ?? "") ||
		engageWhatsAppPhone(item.customerHandle ?? "") ||
		engageWhatsAppPhone(item.customerName)
	);
}

function participantLabel(
	person: { providerId: string; displayName: string; phone?: string; username?: string },
	network?: string,
): string {
	const name = person.displayName.trim();
	if (name && !/^\+?\d{6,}$/.test(name)) return name;
	if (person.phone) return person.phone;
	if (person.username) return `@${person.username.replace(/^@/, "")}`;
	if (network === "whatsapp") {
		return engageWhatsAppPhone(person.providerId) || engagePersonLabel(person) || "Unknown";
	}
	return "Unknown";
}

function participantNumber(
	person: { providerId: string; displayName: string; phone?: string; username?: string },
	network?: string,
): string {
	if (person.phone) return person.phone;
	if (person.username) return `@${person.username.replace(/^@/, "")}`;
	if (network === "whatsapp") return engageWhatsAppPhone(person.providerId);
	return "";
}

function sameEngageSender(left: EngageMessageDto | undefined, right: EngageMessageDto): boolean {
	if (!left || left.direction === "outbound" || right.direction === "outbound") return false;
	const leftHandle = left.senderHandle?.trim() ?? "";
	const rightHandle = right.senderHandle?.trim() ?? "";
	if (leftHandle && rightHandle) return leftHandle === rightHandle;
	const leftName = usableSenderName(left.senderName);
	const rightName = usableSenderName(right.senderName);
	return Boolean(leftName && rightName && leftName === rightName);
}

function previousEngageThreadItem(
	items: ReturnType<typeof threadItems>,
	index: number,
): EngageMessageDto | undefined {
	for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
		const item = items[cursor];
		if (!item) continue;
		if (item.kind === "day") return undefined;
		if (item.kind === "album") return item.messages[item.messages.length - 1];
		return item.message;
	}
	return undefined;
}

function showEngageSender(
	message: EngageMessageDto,
	previous: EngageMessageDto | undefined,
	isGroup: boolean,
): boolean {
	if (!isGroup || message.direction === "outbound") return false;
	return !sameEngageSender(previous, message);
}

function usableSenderName(name: string | null | undefined): string {
	const trimmed = name?.trim() ?? "";
	if (!trimmed || /^\+?\d{6,}$/.test(trimmed) || trimmed.includes("@")) return "";
	return trimmed;
}

function senderLabel(
	message: EngageMessageDto,
	people: Array<{ providerId: string; displayName: string }> | undefined,
	contacts?: EngageConversationDto[],
	thread?: EngageMessageDto[],
): string {
	const handle = message.senderHandle?.trim() ?? "";
	const handlePhone = engageWhatsAppPhone(handle);
	const fromPeople = handle
		? (people ?? []).find((person) => {
				if (person.providerId === handle) return true;
				const personPhone = engageWhatsAppPhone(person.providerId);
				return Boolean(handlePhone && personPhone && personPhone === handlePhone);
			})
		: undefined;
	const fromContact = handle
		? (contacts ?? []).find((row) => {
				if (row.kind === "group") return false;
				return row.customerHandle === handle || row.customerPhone === `+${handle}`;
			})
		: undefined;
	const fromThread = handle
		? (thread ?? []).find((row) => row.senderHandle === handle && usableSenderName(row.senderName))
		: undefined;
	return (
		usableSenderName(message.senderName) ||
		(fromPeople ? usableSenderName(participantLabel(fromPeople)) : "") ||
		usableSenderName(fromContact?.customerName) ||
		usableSenderName(fromThread?.senderName)
	);
}

function FilterTab<T extends string>({
	current,
	value,
	onSelect,
	children,
}: {
	current: T;
	value: T;
	onSelect: (value: T) => void;
	children: string;
}) {
	return (
		<button type="button" role="tab" aria-selected={current === value} onClick={() => onSelect(value)}>
			{children}
		</button>
	);
}

function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const first = parts[0];
	const second = parts[1];
	if (first && second) return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
	return (name.slice(0, 2) || "?").toUpperCase();
}

function toneOf(name: string): number {
	let n = 0;
	for (let i = 0; i < name.length; i += 1) n = (n * 31 + (name.charCodeAt(i) ?? 0)) >>> 0;
	return n % 6;
}

function networkMark(network: string): string {
	const marks: Record<string, string> = {
		whatsapp: "WA",
		instagram: "IG",
		telegram: "TG",
		messenger: "MS",
		x: "X",
		linkedin: "in",
		threads: "Th",
		tiktok: "TT",
	};
	return marks[network] ?? network.slice(0, 2).toUpperCase();
}

function parseWhen(value: string): Date | null {
	if (!value) return null;
	const raw = value.includes("T") ? value : value.replace(" ", "T");
	const date = new Date(/Z$|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}+08:00`);
	return Number.isNaN(date.getTime()) ? null : date;
}

function clockTime(value: string): string {
	const date = parseWhen(value);
	if (!date) return value;
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).format(date);
}

function engageLoginExpiryLabel(expiresAt: string, nowMs: number, method: string): string {
	const end = Date.parse(expiresAt);
	if (!Number.isFinite(end)) return `Code expires ${listTime(expiresAt)}.`;
	const remainingMs = end - nowMs;
	const refreshHint = method === "qr" ? "Refresh the QR code to continue." : "Start again to get a new code.";
	if (remainingMs <= 0) return `Code expired. ${refreshHint}`;
	const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
	if (totalSec < 60) return `Code expires in ${totalSec}s.`;
	const minutes = Math.floor(totalSec / 60);
	const seconds = totalSec % 60;
	return `Code expires in ${minutes}:${String(seconds).padStart(2, "0")}.`;
}

function EngageLoginExpiry({ expiresAt, method }: { expiresAt: string; method: string }) {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		setNow(Date.now());
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, [expiresAt]);
	const expired = Date.parse(expiresAt) - now <= 0;
	return (
		<p className={expired ? "engage-link-foot is-expired" : "engage-link-foot"} aria-live="polite">
			{engageLoginExpiryLabel(expiresAt, now, method)}
		</p>
	);
}

function listTime(value: string): string {
	const date = parseWhen(value);
	if (!date) return value;
	const day = isoDay(date);
	if (day === isoDay(new Date())) {
		return new Intl.DateTimeFormat("en-GB", {
			timeZone: "Asia/Kuala_Lumpur",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		}).format(date);
	}
	if (day === isoDay(new Date(Date.now() - 86_400_000))) return "Yesterday";
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		day: "2-digit",
		month: "short",
	}).format(date);
}

function dayHeading(value: string): string {
	const day = isoDay(value);
	if (day && day === isoDay(new Date())) return "Today";
	if (day && day === isoDay(new Date(Date.now() - 86_400_000))) return "Yesterday";
	return stamp(value).replace(/,\s*\d{2}:\d{2}$/, "") || day;
}

function viewerWhen(value: string): string {
	return `${dayHeading(value)} at ${clockTime(value)}`;
}

function canWheelZoom(): boolean {
	return Boolean(typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)")?.matches);
}

function clampMediaZoom(value: number): number {
	return Math.min(4, Math.max(1, Math.round(value * 100) / 100));
}

function nextMediaZoom(current: number, deltaY: number): number {
	return clampMediaZoom(current * (deltaY < 0 ? 1.12 : 1 / 1.12));
}

function pinchZoom(startZoom: number, startDist: number, dist: number): number {
	return clampMediaZoom(startZoom * (dist / Math.max(startDist, 1)));
}

function touchDistance(a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }): number {
	return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

const ENGAGE_ALBUM_MAX = 6;

function EngageRichText({
	body,
	entities,
}: {
	body: string;
	entities?: EngageMessageDto["textEntities"];
}) {
	return (
		<span className="team-bubble-body">
			{engageFormatText(body, entities).flatMap((part, index) => {
				const chunks = part.marks.includes("code") ? [{ text: part.text }] : engageSplitHttpLinks(part.text);
				return chunks.map((chunk, chunkIndex) => {
					let node: ReactElement | string = chunk.text;
					if (chunk.href) {
						node = (
							<a href={chunk.href} target="_blank" rel="noreferrer">
								{chunk.text}
							</a>
						);
					}
					if (part.marks.includes("code")) node = <code>{node}</code>;
					if (part.marks.includes("italic")) node = <i>{node}</i>;
					if (part.marks.includes("bold")) node = <b>{node}</b>;
					if (part.marks.includes("underline")) node = <u>{node}</u>;
					if (part.marks.includes("strike")) node = <s>{node}</s>;
					return <Fragment key={`${index}-${chunkIndex}-${chunk.text}`}>{node}</Fragment>;
				});
			})}
		</span>
	);
}

function engageVisibleCaption(body: string | null | undefined, mediaName?: string | null): string {
	const text = body?.trim() ?? "";
	if (!text || text === "Image" || text === "File" || text === "Sticker" || text === "Video" || text === "Video message" || text === "GIF" || text === "Audio" || text === "Voice message" || text === "Attachment") return "";
	if (mediaName && text === mediaName) return "";
	return text;
}

function engageAlbumCaption(messages: EngageMessageDto[]): string {
	return messages.reduce((text, message) => engageVisibleCaption(message.body) || text, "");
}

function canJoinEngageAlbum(prev: EngageMessageDto, next: EngageMessageDto): boolean {
	if (!isEngagePhoto(prev) || !isEngagePhoto(next)) return false;
	if (prev.deletedAt || next.deletedAt) return false;
	if (prev.direction !== next.direction) return false;
	if ((prev.senderHandle ?? "") !== (next.senderHandle ?? "")) return false;
	const start = Date.parse(prev.createdAt);
	const end = Date.parse(next.createdAt);
	return Number.isFinite(start) && Number.isFinite(end) && end - start <= 300_000;
}

function canAttachAlbumCaption(prev: EngageMessageDto, next: EngageMessageDto): boolean {
	if (next.messageType === "media" || next.deletedAt) return false;
	if (!engageVisibleCaption(next.body)) return false;
	if (prev.direction !== next.direction) return false;
	const prevHandle = prev.senderHandle ?? "";
	const nextHandle = next.senderHandle ?? "";
	if (prevHandle && nextHandle && prevHandle !== nextHandle) return false;
	const start = Date.parse(prev.createdAt);
	const end = Date.parse(next.createdAt);
	return Number.isFinite(start) && Number.isFinite(end) && end - start <= 15_000;
}

function threadItems(messages: EngageMessageDto[]) {
	const items: Array<
		| { kind: "day"; id: string; label: string }
		| { kind: "message"; message: EngageMessageDto }
		| { kind: "album"; id: string; messages: EngageMessageDto[] }
	> = [];
	let lastDay = "";
	let pending: EngageMessageDto[] = [];

	function flush() {
		const first = pending[0];
		if (!first) return;
		if (pending.length === 1) items.push({ kind: "message", message: first });
		else items.push({ kind: "album", id: first.id, messages: pending });
		pending = [];
	}

	for (const message of messages) {
		if (message.deletedAt || engageHiddenMessage(message)) continue;
		const day = isoDay(message.createdAt);
		if (day && day !== lastDay) {
			flush();
			items.push({ kind: "day", id: `day-${day}`, label: dayHeading(message.createdAt) });
			lastDay = day;
		}
		const albumable = isEngagePhoto(message);
		const last = pending[pending.length - 1];
		if (albumable && last && pending.length < ENGAGE_ALBUM_MAX && canJoinEngageAlbum(last, message)) {
			pending.push(message);
			continue;
		}
		if (!albumable && last && canAttachAlbumCaption(last, message)) {
			const captioned = pending[pending.length - 1];
			if (captioned) pending[pending.length - 1] = { ...captioned, body: message.body };
			flush();
			continue;
		}
		flush();
		if (albumable) {
			pending = [message];
			continue;
		}
		items.push({ kind: "message", message });
	}
	flush();
	return items;
}

type EngageThreadItem = ReturnType<typeof threadItems>[number];

function engageThreadItemHit(item: EngageThreadItem, hitId: string): boolean {
	if (!hitId || item.kind === "day") return false;
	if (item.kind === "message") return item.message.id === hitId;
	return item.id === hitId || item.messages.some((message) => message.id === hitId);
}

function engageFindHighlight(text: string, needle: string) {
	const query = needle.trim();
	if (!query) return text;
	const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	return text.split(new RegExp(`(${escaped})`, "ig")).map((chunk, index) =>
		chunk.toLowerCase() === query.toLowerCase() ? <mark key={index}>{chunk}</mark> : chunk,
	);
}

function engageListItemIdForMessage(messages: EngageMessageDto[], messageId: string): string {
	for (const item of threadItems(messages)) {
		if (item.kind === "message" && item.message.id === messageId) return messageId;
		if (item.kind === "album" && item.messages.some((message) => message.id === messageId)) return item.id;
	}
	return messageId;
}

function statusLabel(status: string, network?: string): string {
	if (status === "sent") return "Sent";
	if (status === "delivered") return "Delivered";
	if (status === "read") return network === "telegram" ? "Seen" : "Read";
	if (status === "failed") return "Failed";
	if (status === "queued" || status === "sending") return "Sending";
	return status;
}

function engageAccountShowsDisconnect(account: EngageAccountDto, userId: string | undefined): boolean {
	if (account.canDisconnect === false) return false;
	if (account.canDisconnect === true) return true;
	return !account.ownerUserId || account.ownerUserId === userId;
}

function accountStatusLabel(status: string): string {
	if (status === "active") return "Connected";
	if (status === "degraded") return "Offline";
	if (status === "expired") return "Disconnected";
	if (status === "disconnected") return "Disconnected";
	if (status === "connecting" || status === "syncing") return "Connecting";
	return status;
}

const lastMarkedOpenChat = { key: "", at: 0 };

function markOpenEngageChatRead(conversationId: string, inboundId: string, queryClient: QueryClient) {
	if (!conversationId) return;
	if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
	const now = Date.now();
	const key = `${conversationId}:${inboundId}`;
	if (lastMarkedOpenChat.key === key && now - lastMarkedOpenChat.at < 2500) return;
	lastMarkedOpenChat.key = key;
	lastMarkedOpenChat.at = now;
	queryClient.setQueryData(queryKeys.engageConversations, (rows: EngageConversationDto[] | undefined) =>
		rows?.map((row) => (row.id === conversationId ? { ...row, unread: false, unreadCount: 0 } : row)),
	);
	void api.markEngageConversationRead(conversationId).then(() => {
		queryClient.setQueryData(queryKeys.engageConversations, (rows: EngageConversationDto[] | undefined) =>
			rows?.map((row) => (row.id === conversationId ? { ...row, unread: false, unreadCount: 0 } : row)),
		);
	});
}

function visibilityInterval(): number | false {
	if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
	return 4000;
}

const ENGAGE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const ENGAGE_FILE_MAX_BYTES = 12_000_000;

function engageHiddenMessage(message: EngageMessageDto): boolean {
	return engageIsPlaceholderBody(message.body) && !message.mediaSha256 && !message.mediaMime;
}

function isEngageSpecial(message: EngageMessageDto): boolean {
	const mime = message.mediaMime ?? "";
	return mime === ENGAGE_LOCATION_MIME || mime === ENGAGE_CONTACT_MIME || mime === ENGAGE_POLL_MIME;
}

function isEngageVideo(message: EngageMessageDto): boolean {
	return (message.mediaMime ?? "").startsWith("video/");
}

function isEngageAudio(message: EngageMessageDto): boolean {
	return (message.mediaMime ?? "").startsWith("audio/");
}

function isEngagePhoto(message: EngageMessageDto): boolean {
	if (message.messageType !== "media" || message.deletedAt) return false;
	if (isEngageSpecial(message) || isEngageVideo(message) || isEngageAudio(message)) return false;
	return !isEngageFile(message);
}

function isEngageFile(message: EngageMessageDto): boolean {
	if (message.messageType !== "media" || message.deletedAt) return false;
	if (engageLooksLikeImageMime(message.mediaMime) || isEngageVideo(message) || isEngageAudio(message) || isEngageSpecial(message)) {
		return false;
	}
	return Boolean(message.mediaName) || Boolean(message.mediaMime);
}

function engageFileName(message: EngageMessageDto): string {
	return message.mediaName?.trim() || (message.body !== "File" ? message.body.trim() : "") || "File";
}

async function readFileBytes(file: Blob): Promise<ArrayBuffer> {
	if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
	return new Response(file).arrayBuffer();
}

async function fileToEngageAttachment(file: File): Promise<{
	mimeType: string;
	bytesBase64: string;
	filename: string;
}> {
	const mime = file.type === "image/jpg" ? "image/jpeg" : file.type || "application/octet-stream";
	if (file.size > ENGAGE_FILE_MAX_BYTES) {
		throw new Error("That file is larger than 12 MB.");
	}
	const bytes = new Uint8Array(await readFileBytes(file));
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return {
		mimeType: mime,
		bytesBase64: btoa(binary),
		filename: file.name.replace(/[\\/]/g, "").slice(0, 200) || "file",
	};
}

function EngageFile({
	conversationId,
	message,
}: {
	conversationId: string;
	message: EngageMessageDto;
}) {
	const name = engageFileName(message);
	const [busy, setBusy] = useState(false);
	async function download() {
		if (!message.mediaSha256 || busy) return;
		setBusy(true);
		try {
			const blob = await api.readEngageMedia(conversationId, message.id);
			const url = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "";
			if (!url) return;
			const link = document.createElement("a");
			link.href = url;
			link.download = name;
			link.rel = "noopener";
			link.style.display = "none";
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} finally {
			setBusy(false);
		}
	}
	return (
		<button
			className="engage-file"
			type="button"
			onClick={() => void download()}
			disabled={!message.mediaSha256 || busy}
			aria-label={`Download ${name}`}
		>
			<DocumentDownload size={22} aria-hidden="true" />
			<span>
				<strong>{name}</strong>
				<span>{message.mediaSha256 ? (busy ? "Preparing" : "Download") : "File"}</span>
			</span>
		</button>
	);
}

function EngageSpecial({ message }: { message: EngageMessageDto }) {
	const mime = message.mediaMime ?? "";
	const title = message.body.trim() || "Message";
	if (mime === ENGAGE_LOCATION_MIME) {
		const pin = message.mediaName?.trim() ?? "";
		const href = pin ? `https://maps.google.com/?q=${encodeURIComponent(pin)}` : "";
		const card = (
			<>
				<strong>Location</strong>
				<span>{title}</span>
			</>
		);
		return href ? (
			<a className="engage-card" href={href} target="_blank" rel="noreferrer">
				{card}
			</a>
		) : (
			<div className="engage-card">{card}</div>
		);
	}
	if (mime === ENGAGE_CONTACT_MIME) {
		const phone = message.mediaName?.trim() ?? "";
		const card = (
			<>
				<strong>Contact</strong>
				<span>{title}</span>
				{phone ? <span>{phone}</span> : null}
			</>
		);
		return phone ? (
			<a className="engage-card" href={`tel:${phone}`}>
				{card}
			</a>
		) : (
			<div className="engage-card">{card}</div>
		);
	}
	const options = (message.mediaName ?? "").split("\n").map((item) => item.trim()).filter(Boolean);
	return (
		<div className="engage-card">
			<strong>Poll</strong>
			<span>{title}</span>
			{options.length ? (
				<ul>
					{options.map((option) => (
						<li key={option}>{option}</li>
					))}
				</ul>
			) : null}
		</div>
	);
}

function EngagePlayback({
	conversationId,
	message,
}: {
	conversationId: string;
	message: EngageMessageDto;
}) {
	const [url, setUrl] = useState("");
	const video = isEngageVideo(message);
	useEffect(() => {
		if (!message.mediaSha256) return;
		let objectUrl = "";
		let gone = false;
		void (async () => {
			try {
				const blob = await api.readEngageMedia(conversationId, message.id);
				if (gone) return;
				objectUrl = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "";
				setUrl(objectUrl);
			} catch {
				setUrl("");
			}
		})();
		return () => {
			gone = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [conversationId, message.id, message.mediaSha256]);
	if (video) {
		if (!url) return <span className="team-media-wait">{message.body || "Video"}</span>;
		return (
			<div className="engage-play">
				<video src={url} controls playsInline preload="metadata" />
			</div>
		);
	}
	return <EngageAudioPlayer url={url} />;
}

function EngageMedia({
	conversationId,
	message,
	onOpen,
}: {
	conversationId: string;
	message: EngageMessageDto;
	onOpen: () => void;
}) {
	const [url, setUrl] = useState("");
	useEffect(() => {
		if (!message.mediaSha256) return;
		let objectUrl = "";
		let gone = false;
		void (async () => {
			for (let attempt = 0; attempt < 4; attempt += 1) {
				try {
					const blob = await api.readEngageMedia(conversationId, message.id);
					if (gone) return;
					objectUrl = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "";
					setUrl(objectUrl);
					return;
				} catch {
					await new Promise((resolve) => window.setTimeout(resolve, 280 * (attempt + 1)));
				}
			}
		})();
		return () => {
			gone = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [conversationId, message.id, message.mediaSha256]);
	if (!url) return <span className="team-media-wait">Photo</span>;
	return (
		<button className="team-media-open" type="button" aria-label="Open photo" onClick={onOpen}>
			<img className="team-media" src={url} alt="" />
		</button>
	);
}

function EngageMediaViewer({
	conversationId,
	items,
	currentId,
	onClose,
	onSelect,
}: {
	conversationId: string;
	items: EngageMessageDto[];
	currentId: string;
	onClose: () => void;
	onSelect: (id: string) => void;
}) {
	const index = Math.max(0, items.findIndex((item) => item.id === currentId));
	const message = items[index];
	const [url, setUrl] = useState("");
	const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
	const viewRef = useRef(view);
	const gestureRef = useRef<
		| { kind: "pan"; lastX: number; lastY: number }
		| {
				kind: "pinch";
				startZoom: number;
				startX: number;
				startY: number;
				startDist: number;
				startMidX: number;
				startMidY: number;
		  }
		| null
	>(null);
	const stageRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!items.length || !items.some((item) => item.id === currentId)) onClose();
	}, [currentId, items, onClose]);
	useEffect(() => {
		const next = { zoom: 1, x: 0, y: 0 };
		viewRef.current = next;
		setView(next);
		gestureRef.current = null;
	}, [message?.id]);
	useEffect(() => {
		if (!message) return;
		let objectUrl = "";
		let gone = false;
		setUrl("");
		void (async () => {
			for (let attempt = 0; attempt < 4; attempt += 1) {
				try {
					const blob = await api.readEngageMedia(conversationId, message.id);
					if (gone) return;
					objectUrl = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "";
					setUrl(objectUrl);
					return;
				} catch {
					await new Promise((resolve) => window.setTimeout(resolve, 280 * (attempt + 1)));
				}
			}
		})();
		return () => {
			gone = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [conversationId, message?.id]);
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
			if (event.key === "ArrowLeft") {
				const next = items[index - 1];
				if (next) onSelect(next.id);
			}
			if (event.key === "ArrowRight") {
				const next = items[index + 1];
				if (next) onSelect(next.id);
			}
		};
		window.addEventListener("keydown", onKey);
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKey);
			document.body.style.overflow = previous;
		};
	}, [index, items, onClose, onSelect]);
	useEffect(() => {
		const stage = stageRef.current;
		if (!stage) return;
		const apply = (next: { zoom: number; x: number; y: number }) => {
			const clean = next.zoom <= 1 ? { zoom: 1, x: 0, y: 0 } : next;
			viewRef.current = clean;
			setView(clean);
		};
		const onWheel = (event: WheelEvent) => {
			if (!canWheelZoom()) return;
			event.preventDefault();
			const current = viewRef.current;
			apply({ ...current, zoom: nextMediaZoom(current.zoom, event.deltaY) });
		};
		const onPointerDown = (event: globalThis.PointerEvent) => {
			if (event.pointerType === "touch") return;
			if (viewRef.current.zoom <= 1) return;
			if (!(event.target instanceof Element) || !event.target.closest("img")) return;
			gestureRef.current = { kind: "pan", lastX: event.clientX, lastY: event.clientY };
			if (typeof stage.setPointerCapture === "function") stage.setPointerCapture(event.pointerId);
		};
		const onPointerMove = (event: globalThis.PointerEvent) => {
			const gesture = gestureRef.current;
			if (!gesture || gesture.kind !== "pan") return;
			const current = viewRef.current;
			apply({ ...current, x: current.x + event.clientX - gesture.lastX, y: current.y + event.clientY - gesture.lastY });
			gesture.lastX = event.clientX;
			gesture.lastY = event.clientY;
		};
		const endPan = () => {
			if (gestureRef.current?.kind === "pan") gestureRef.current = null;
		};
		const onTouchStart = (event: TouchEvent) => {
			const first = event.touches[0];
			const second = event.touches[1];
			if (first && second) {
				event.preventDefault();
				gestureRef.current = {
					kind: "pinch",
					startZoom: viewRef.current.zoom,
					startX: viewRef.current.x,
					startY: viewRef.current.y,
					startDist: touchDistance(first, second),
					startMidX: (first.clientX + second.clientX) / 2,
					startMidY: (first.clientY + second.clientY) / 2,
				};
				return;
			}
			if (first && viewRef.current.zoom > 1) {
				gestureRef.current = { kind: "pan", lastX: first.clientX, lastY: first.clientY };
			}
		};
		const onTouchMove = (event: TouchEvent) => {
			const gesture = gestureRef.current;
			if (!gesture) return;
			const first = event.touches[0];
			const second = event.touches[1];
			if (gesture.kind === "pinch" && first && second) {
				event.preventDefault();
				apply({
					zoom: pinchZoom(gesture.startZoom, gesture.startDist, touchDistance(first, second)),
					x: gesture.startX + (first.clientX + second.clientX) / 2 - gesture.startMidX,
					y: gesture.startY + (first.clientY + second.clientY) / 2 - gesture.startMidY,
				});
				return;
			}
			if (gesture.kind === "pan" && first) {
				event.preventDefault();
				apply({
					...viewRef.current,
					x: viewRef.current.x + first.clientX - gesture.lastX,
					y: viewRef.current.y + first.clientY - gesture.lastY,
				});
				gesture.lastX = first.clientX;
				gesture.lastY = first.clientY;
			}
		};
		const onTouchEnd = (event: TouchEvent) => {
			if (event.touches.length < 2 && gestureRef.current?.kind === "pinch") gestureRef.current = null;
			if (event.touches.length === 0) gestureRef.current = null;
		};
		stage.addEventListener("wheel", onWheel, { passive: false });
		stage.addEventListener("pointerdown", onPointerDown);
		stage.addEventListener("pointermove", onPointerMove);
		stage.addEventListener("pointerup", endPan);
		stage.addEventListener("pointercancel", endPan);
		stage.addEventListener("touchstart", onTouchStart, { passive: false });
		stage.addEventListener("touchmove", onTouchMove, { passive: false });
		stage.addEventListener("touchend", onTouchEnd);
		stage.addEventListener("touchcancel", onTouchEnd);
		return () => {
			stage.removeEventListener("wheel", onWheel);
			stage.removeEventListener("pointerdown", onPointerDown);
			stage.removeEventListener("pointermove", onPointerMove);
			stage.removeEventListener("pointerup", endPan);
			stage.removeEventListener("pointercancel", endPan);
			stage.removeEventListener("touchstart", onTouchStart);
			stage.removeEventListener("touchmove", onTouchMove);
			stage.removeEventListener("touchend", onTouchEnd);
			stage.removeEventListener("touchcancel", onTouchEnd);
		};
	}, [url]);
	function step(delta: number) {
		const next = items[index + delta];
		if (next) onSelect(next.id);
	}
	if (!message) return null;
	const who = message.direction === "outbound" ? "You" : usableSenderName(message.senderName) || "Chat";
	const caption = message.body && message.body !== "Image" ? message.body : "";
	return (
		<div
			className="team-media-view"
			role="dialog"
			aria-modal="true"
			aria-label="Photo"
			onPointerDown={(event) => event.stopPropagation()}
		>
			<div className="team-media-topbar">
				<span className={`team-avatar tone-${toneOf(who)}`} aria-hidden="true">
					{initials(who)}
				</span>
				<div className="team-media-who">
					<strong>{who}</strong>
					<span>
						{index + 1} of {items.length}
						<i className="team-media-dot" aria-hidden="true" />
						{viewerWhen(message.createdAt)}
					</span>
				</div>
				{url ? (
					<a className="team-icon-btn" href={url} download="photo" aria-label="Download">
						<ArrowDown size={20} aria-hidden="true" />
					</a>
				) : null}
				<button className="team-icon-btn" type="button" aria-label="Close photo" onClick={onClose}>
					<span className="team-media-x" aria-hidden="true" />
				</button>
			</div>
			<div
				ref={stageRef}
				className={view.zoom > 1 ? "team-media-stage is-zoom" : "team-media-stage"}
				style={
					{
						"--team-media-zoom": String(view.zoom),
						"--team-media-x": `${view.x}px`,
						"--team-media-y": `${view.y}px`,
					} as CSSProperties
				}
				onClick={(event) => {
					if (event.target === event.currentTarget && view.zoom <= 1) onClose();
				}}
			>
				{items.length > 1 ? (
					<button
						className="team-media-nav"
						type="button"
						aria-label="Previous"
						disabled={index === 0}
						onClick={() => step(-1)}
					>
						<ChevronLeft size={28} aria-hidden="true" />
					</button>
				) : null}
				{url ? <img src={url} alt="" draggable={false} /> : <span className="team-media-wait">Photo</span>}
				{items.length > 1 ? (
					<button
						className="team-media-nav is-next"
						type="button"
						aria-label="Next"
						disabled={index === items.length - 1}
						onClick={() => step(1)}
					>
						<ChevronLeft size={28} aria-hidden="true" />
					</button>
				) : null}
			</div>
			{caption ? <p className="team-media-caption">{caption}</p> : null}
		</div>
	);
}
